/**
 * PUNTO DE VENTA — la pantalla más importante de Softfi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  CÓMO ESTÁ PENSADA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Un cajero con cola esperando no lee: reconoce y toca. Todo aquí está pensado
 * para que una venta se cierre en pocos segundos:
 *
 *  - **Buscador siempre enfocado.** Un lector de código de barras funciona como
 *    un teclado: escribe el código y pulsa Enter. Si el foco está en el buscador,
 *    escanear agrega el producto al carrito sin tocar nada.
 *  - **Todo se busca en el dispositivo**, nunca preguntándole al servidor. Es
 *    instantáneo, y funciona igual con o sin internet.
 *  - **Grilla de botones grandes** con el precio y su equivalente en cada una de
 *    las demás monedas debajo (el cliente pregunta "¿cuánto es en bolívares?").
 *  - **En celular** el carrito es una barra fija abajo con el total y un botón
 *    grande de Cobrar; se despliega al tocarla. En **PC** va a la derecha, fijo.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  SIN INTERNET SE SIGUE VENDIENDO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El catálogo, las monedas con sus tasas y los métodos de pago están guardados en
 * el dispositivo (ver `useDatosPos`). La venta se cobra, se imprime el recibo y
 * queda en una cola que se envía sola al volver la señal.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSucursales } from "@/components/SucursalActual";
import { AlertTriangle, Boxes, Check, ChevronUp, CornerDownLeft, Layers, Minus, Package, Plus, RefreshCw, Search, SearchX, ShoppingCart, Trash2, User, Warehouse, WifiOff, X } from "lucide-react";
import {
  Boton,
  Campo,
  cx,
  Esqueleto,
  Insignia,
  Lista,
  Nota,
  Tarjeta,
  Vacio,
} from "@/components/ui";
import { caja, catalogo, equipo, impuestos, listasDePrecios, negocio } from "@/lib/endpoints";
import {
  comoFormato,
  comoFormatoJuntoA,
  equivalenteQueSeCobra,
  formatMoney,
  formatQty,
  monedaPrincipal,
  MONEDA_POR_DEFECTO,
  toNumber,
} from "@/lib/money";
import { useLimpiarMonto } from "@/lib/pasoDeMoneda";
import type { ItemListRow, Sale, Tax } from "@/lib/types";
import {
  calcularTotales,
  desgloseDeLinea,
  redondearMonto,
  useCarrito,
  usaPrecioDeLista,
  type LineaCarrito,
} from "@/stores/carrito";
import { comoEntraLoEscaneado } from "./comoEntraLoEscaneado";
import { categoriasDeLaCaja, entraEnLaCategoria } from "./categoriasDeLaCaja";
import { useAuth } from "@/stores/auth";
import { TasaDelDia } from "@/features/monedas/TasaDelDia";
import { useTecladoADemanda } from "@/lib/tecladoADemanda";
import { useAlLeerCodigo } from "@/lib/lectorGlobal";
import { useConexion } from "@/stores/conexion";
import { aviso } from "@/stores/ui";
import { exentosComoDinero, InterruptoresDeImpuesto } from "./InterruptoresDeImpuesto";
import { recargoDeImpuestosDePedido } from "./impuestosDePedido";
import { ModalPago } from "./ModalPago";
import { GuardarApartado } from "@/features/apartados/GuardarApartado";
import { ModalRecibo } from "./Recibo";
import { TOPE_PRODUCTOS, useDatosPos } from "./useDatosPos";
import { BotonGuardarCarrito, ListaDeCarritos } from "./CarritosGuardados";
import { BotonHacerCotizacion } from "@/features/cotizaciones/BotonHacerCotizacion";
import { ModalCotizacion } from "@/features/cotizaciones/ModalCotizacion";
import { PastillaDeCliente, SelectorDeCliente } from "./SelectorDeCliente";
import { BarraDePeso } from "./BarraDePeso";
import { BarraDeRecarga } from "./BarraDeRecarga";
import { BotonDeEscaner } from "@/components/BotonDeEscaner";
import { seEscaneaConLaCamara } from "@/lib/lecturaDeCodigos";
import { EscanerDeCodigo } from "@/components/EscanerDeCodigo";
import {
  comoSeDice,
  etiquetaDeUnidad,
  sePreguntaCuanto,
  unidadDePeso,
} from "./pesoYDinero";
import { esDeMontoVariable, recordarMonto } from "./recargas";
import {
  cuantoDice,
  iniciales,
  loQueHayEnLaCuenta,
  primeroLoDeLaCuenta,
  tinteDeCategoria,
  type EnLaCuenta,
} from "./catalogoConCuenta";
import { cuantoHay, estaAgotado, estadoDeExistencia, type EstadoDeExistencia } from "./existencia";
import { SelectorDeTalla } from "./SelectorDeTalla";
import { ElegirVariacion, precioConImpuesto } from "@/components/ElegirVariacion";
import { ModalDeOpciones } from "@/features/mesas/ModalDeOpciones";
import { BotonDeSin, ModalDeSin } from "@/features/mesas/QuitarIngredientes";
import { quitablesDelPlato, quitarIngredientesActivo } from "@/lib/funcionesDelRestaurante";
import { SelectorDePresentacion } from "./SelectorDePresentacion";
import {
  deQuienEsCadaCaja,
  enUnidadesDelProducto,
  presentacionesPorProducto,
} from "./presentaciones";
import { sucursalParaVender } from "@/stores/sucursal";
import { contieneSinTildes } from "@/lib/sinTildes";
import { BotonDeAyuda } from "@/features/ayuda/BotonDeAyuda";

/**
 * Cuántas tarjetas se pintan de una vez.
 *
 * Sesenta llenan de sobra la primera pantalla en cualquier tamaño, y el
 * navegador las maqueta sin despeinarse. Ver el comentario largo dentro del
 * componente.
 */
const POR_TANDA = 60;

/**
 * Vender, salvo en un almacén (2026-10-01): ahí no se vende. El menú ya
 * esconde «Vender»; esto es para quien llega por un enlace guardado.
 */
export function PosPage() {
  const { actual } = useSucursales();
  if (actual?.tipo === "almacen") {
    return (
      <Vacio
        icono={<Warehouse size={24} />}
        titulo={`${actual.name} es un almacén`}
        descripcion="En un almacén no se vende ni se cotiza. Para vender, seleccione una sucursal arriba a la izquierda."
      />
    );
  }
  return <PosDeLaSucursal />;
}

function PosDeLaSucursal() {
  const { tenant } = useAuth();
  const qc = useQueryClient();
  const enLinea = useConexion((s) => s.enLinea);

  const carrito = useCarrito();
  const totales = useMemo(
    () =>
      calcularTotales(carrito.lineas, carrito.descuentoGlobal, carrito.listaPreciosId),
    // ⚠️ La lista TIENE que estar en las dependencias: sin ella, cambiar de
    // «Normal» a «Mayor» no recalcularía el total y la pantalla seguiria
    // enseñando el precio viejo.
    [carrito.lineas, carrito.descuentoGlobal, carrito.listaPreciosId],
  );

  /**
   * El producto cuya talla se está eligiendo.
   *
   * ⚠️ Tocar un producto con tallas NO lo mete al carrito: abre esta pantalla.
   *
   * Meterlo directo y dejar que el cajero corrija después es lo que hace que
   * las tallas no sirvan: la venta sale sin decir cuál se despachó y el
   * inventario de las 20 casillas se queda igual. Un toque más en el mostrador
   * es lo que hace que el trabajo del catálogo valga para algo.
   */
  const [eligiendoTalla, setEligiendoTalla] = useState<ItemListRow | null>(null);
  const [eligiendoVariacion, setEligiendoVariacion] = useState<ItemListRow | null>(null);
  /**
   * El plato al que se le preguntan los extras («+ Queso»), con la variación
   * ya elegida si tiene (2026-10-15). Sin extras entra de un toque, como siempre.
   */
  const [pidiendoExtras, setPidiendoExtras] = useState<{
    item: ItemListRow;
    variacion: { id: string; nombre: string; precio: string } | null;
  } | null>(null);
  /**
   * El producto al que se le está preguntando «¿suelto o caja?». Solo los que
   * el dueño configuró con caja o tira. Ver `SelectorDePresentacion`.
   */
  const [eligiendoCaja, setEligiendoCaja] = useState<ItemListRow | null>(null);
  /** El producto al peso cuya cantidad se está pidiendo. */
  const [pesando, setPesando] = useState<ItemListRow | null>(null);
  /*
    Qué recarga está preguntando de cuánto es.

    Va aparte de `pesando` aunque las dos franjas se parezcan: son dos
    preguntas distintas —cuánto pesa y de cuánto es— y juntarlas en un solo
    estado obligaría a preguntar «¿y esta cuál de las dos era?» en cada sitio
    donde se usa.
  */
  const [recargando, setRecargando] = useState<ItemListRow | null>(null);
  /*
    Si la cámara está abierta escaneando.

    ⚠️ Aquí se queda abierta después de cada lectura, y en los buscadores
    del catálogo no. Es la misma diferencia que entre pesar y buscar: aquí
    se pasan diez productos seguidos, y cerrar y encender la cámara diez
    veces es un segundo de espera por cada uno.
  */
  const [escaneando, setEscaneando] = useState(false);


  const [busqueda, setBusqueda] = useState("");
  const [categoria, setCategoria] = useState("");
  // La subcategoría elegida dentro de `categoria`, si se tocó una. Ver
  // `categoriasDeLaCaja`.
  const [subcategoria, setSubcategoria] = useState("");
  const [carritoAbierto, setCarritoAbierto] = useState(false);
  const [pagoAbierto, setPagoAbierto] = useState(false);

  /*
    Cobrando en un PC: es cuando el catálogo se aparta y entra el resumen.

    En el celular no pasa nada de esto — allá el cobro ocurre dentro de la hoja
    que sube desde abajo, que ya ES la pantalla entera. `carritoAbierto` solo
    puede ser cierto ahí, porque el botón que la abre es `lg:hidden`.

    ⚠️ Va aquí arriba y no donde se usa: el efecto que esconde el catálogo
    depende de esto, y una `const` leída antes de su línea revienta.
  */
  const cobrandoEnPc = pagoAbierto && !carritoAbierto;

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ LA COTIZACIÓN RECIÉN HECHA VIVE **AQUÍ**, NO EN SU BOTÓN
    ═══════════════════════════════════════════════════════════════════════════

    Y ese fue un fallo de verdad, de los que no dan ningún error.

    El botón «Hacer cotización» abría él mismo la ventana con el papel. Pero el
    botón vive dentro del bloque del carrito, que solo se dibuja **cuando hay
    líneas**. Guardar la cotización vacía el carrito → el bloque desaparece → y
    se lleva la ventana por delante en el mismo instante.

    Resultado: el papel se abría y moría solo, y para imprimirlo había que
    irse a Cotizaciones y buscarlo. El dueño: «eso solo son pasos de más si
    estoy haciendo una cotización».

    `PosPage` no se desmonta por vaciar un carrito. Es el mismo sitio donde ya
    vivía `ventaHecha` por el mismo motivo.
  */
  const [cotizacionHecha, setCotizacionHecha] = useState<
    import("@/lib/types").Cotizacion | null
  >(null);

  /*
    ══════════════════════════════════════════════════════════════════════════
     ESCONDER EL CATÁLOGO **DESPUÉS** DE QUE TERMINE DE APARTARSE
    ══════════════════════════════════════════════════════════════════════════

    El catálogo y el resumen comparten celda, así que mientras los dos existen
    la fila mide lo que mida el más alto — y el catálogo con 3.000 productos es
    muchísimo más alto. Si se quedara ahí invisible, debajo del resumen quedaría
    una pantalla y media de vacío por la que se puede desplazar.

    Con `display:none` deja de ocupar. Pero ponerlo a la vez que empieza la
    animación la mata: no se puede animar lo que ya no se dibuja. Así que se
    espera a que termine.
  */
  const [catalogoOculto, setCatalogoOculto] = useState(false);
  useEffect(() => {
    // ⚠️ `cobrandoEnPc` y no `pagoAbierto`: en el celular el cobro pasa DENTRO
    // de la hoja que sube desde abajo, y el catálogo de detrás tiene que
    // seguir estando cuando esa hoja se cierre.
    if (!cobrandoEnPc) {
      // Al volver se muestra YA, sin esperar: tiene que estar dibujado para
      // poder entrar deslizando.
      setCatalogoOculto(false);
      return;
    }
    const t = setTimeout(() => setCatalogoOculto(true), 320);
    return () => clearTimeout(t);
  }, [cobrandoEnPc]);
  const [ventaHecha, setVentaHecha] = useState<Sale | null>(null);

  const buscadorRef = useRef<HTMLInputElement>(null);
  // En el teléfono, el teclado de la pantalla solo al tocar el buscador: el
  // lector (incluido o Bluetooth) escribe igual. Ver `tecladoADemanda.ts`.
  const teclado = useTecladoADemanda(buscadorRef);

  // --- Datos del POS: del dispositivo, refrescados si hay internet ---
  const datos = useDatosPos();

  /*
    Cuando llega el catálogo fresco, las líneas del carrito se ponen al día de
    qué está exento. Ver `refrescarExentos` en el carrito: sin esto, lo que se
    agregó con la copia vieja cobraría el IVA sobre la harina.
  */
  const refrescarExentos = useCarrito((s) => s.refrescarExentos);
  useEffect(() => {
    if (datos.items.length === 0) return;
    refrescarExentos(new Map(datos.items.map((i) => [i.id, i.impuestos_exentos ?? []])));
  }, [datos.items, refrescarExentos]);

  /*
    Corrigiendo una venta: las líneas con los precios de HOY, que son los que
    va a cobrar el servidor. Ver `alinearConElCatalogo` en el carrito.
  */
  // Editando un apartado, lo mismo: lo guarda con los precios de hoy.
  const corrigiendoId = carrito.corrigiendo?.id ?? carrito.editandoApartado?.id ?? null;
  const alinearConElCatalogo = useCarrito((s) => s.alinearConElCatalogo);
  useEffect(() => {
    if (!corrigiendoId || datos.items.length === 0) return;
    alinearConElCatalogo(new Map(datos.items.map((i) => [i.id, i])));
  }, [corrigiendoId, datos.items, alinearConElCatalogo]);

  // El equipo y la caja sí necesitan red; si no hay, simplemente no se usan
  // (el POS no depende de ellos para cobrar).
  const { data: miembros } = useQuery({
    queryKey: ["equipo"],
    queryFn: equipo.listar,
    enabled: enLinea,
  });
  /*
    ⚠️ Aquí había una consulta del negocio para saber si la balanza estaba
    encendida, y con ella se decidía si preguntarle al servidor por un código.

    Se fue con el arreglo del escaneo: ahora se le pregunta por CUALQUIER código
    que la copia local no resuelva, tenga balanza o no. El viaje de más que ese
    interruptor evitaba solo ocurre cuando la búsqueda local ya falló — y ahí
    hay que preguntar igual, porque puede ser la variante de una talla.
  */
  const { data: turnoCaja } = useQuery({
    queryKey: ["caja-actual"],
    queryFn: caja.actual,
    enabled: enLinea,
  });

  const principal = monedaPrincipal(datos.monedas);
  const formato = principal ? comoFormato(principal) : MONEDA_POR_DEFECTO;
  /*
    ⚠️ TODAS LAS MONEDAS EN QUE SE COBRA, NO SOLO LA SEGUNDA (2026-10-12).

    Aquí había un `find`: la primera moneda que no fuera la principal. Un negocio
    que cobra en dólares, bolívares y pesos veía los bolívares en cada tarjeta
    y en el total, y los pesos en ninguna parte. Lo vio el dueño: «solo sale la
    segunda moneda creada».
  */
  const secundarias = useMemo(
    () =>
      datos.monedas
        .filter((m) => !m.is_primary && m.is_active && m.accepts_payment && toNumber(m.current_rate) > 0)
        .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    [datos.monedas],
  );
  /** El SKU y el código de barras de cada producto, para las líneas de la cuenta. */
  const codigosPorProducto = useMemo(
    () => new Map(datos.items.map((i) => [i.id, { sku: i.sku, barcode: i.barcode, conTallas: i.has_variants }])),
    [datos.items],
  );

  /*
    ⚠️ LA CAJA Y LA TIRA NO SON TARJETAS DE LA GRILLA.

    Llegan en el catálogo como filas aparte (así el lector encuentra el código
    de la caja sin internet), pero se pintan DENTRO de su producto: el mismo
    refresco tres veces en la grilla es lo que el dueño pidió evitar. Ver
    `presentaciones.ts`.
  */
  const cajasPorProducto = useMemo(() => presentacionesPorProducto(datos.items), [datos.items]);
  const deQuienEs = useMemo(() => deQuienEsCadaCaja(datos.items), [datos.items]);

  // --- Categorías, sacadas del propio catálogo guardado ---
  // Así la fila de categorías funciona sin conexión sin necesitar otra consulta.
  // Arriba las categorías; al tocar una con subcategorías, sale su fila debajo.
  const { madres: categorias, hijasDe } = useMemo(
    () => categoriasDeLaCaja(datos.items),
    [datos.items],
  );
  const subcategorias = categoria ? (hijasDe.get(categoria) ?? []) : [];

  // --- Filtrado local: instantáneo y con el mismo resultado siempre ---
  const items = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    return datos.items.filter((i) => {
      // La caja vive dentro de su producto (ver arriba).
      if (i.presentacion_de_id) return false;
      if (!entraEnLaCategoria(i, categoria, subcategoria)) return false;
      if (!texto) return true;
      return (
        contieneSinTildes(i.name, texto) ||
        // «polar» trae lo de Polar aunque el nombre no lo diga (2026-10-18).
        contieneSinTildes(i.marca, texto) ||
        contieneSinTildes(i.sku, texto) ||
        contieneSinTildes(i.barcode, texto)
      );
    });
  }, [datos.items, busqueda, categoria, subcategoria]);

  // ═══════════════════════════════════════════════════════════════════════
  //  ⚠️ NO SE PINTAN TODOS LOS PRODUCTOS DE GOLPE
  // ═══════════════════════════════════════════════════════════════════════
  //
  // Con el catálogo real de una papelería —3.440 productos— la grilla creaba
  // 3.444 tarjetas en el primer render. Medido: el navegador se quedaba
  // bloqueado más de 45 segundos recalculando la maquetación, y cada tecla en
  // el buscador lo repetía.
  //
  // Y no servía de nada: nadie recorre tres mil tarjetas con un cliente
  // delante. Se busca por nombre, se escanea, o se toca una categoría.
  //
  // Así que se pinta una tanda y crece sola al llegar al final. La búsqueda
  // sigue mirando el catálogo ENTERO —eso no se toca— y solo se limita lo que
  // se dibuja.
  const [cuantasPintar, setCuantasPintar] = useState(POR_TANDA);

  // Al cambiar el filtro se vuelve a empezar: si no, quien busca algo después
  // de haber bajado mucho se encuentra con seiscientas tarjetas de golpe.
  useEffect(() => {
    setCuantasPintar(POR_TANDA);
  }, [busqueda, categoria, subcategoria]);

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ LO QUE ESTÁ EN LA CUENTA SUBE ARRIBA, Y EN VERDE
    ═══════════════════════════════════════════════════════════════════════════

    De más a menos cantidad —«los 6 bolis y luego los de 2», como lo pidió el
    dueño— y con cuántos hay. El cajero ve qué lleva el cliente sin mirar a la
    otra columna, y el «otro más» está arriba del todo.

    ⚠️ Se reordena la lista YA FILTRADA: si está escrito «coca», la harina de la
    cuenta no sale, porque no coincide. Y se hace ANTES de cortar la tanda que
    se pinta: si no, un producto de la cuenta que caía en la tarjeta 300 no
    subiría nunca. Ver `catalogoConCuenta.ts`.
  */
  // ⚠️ Una caja en la cuenta cuenta como sus 24 unidades en la tarjeta del
  // producto: sube arriba, sale en verde y se compara con el stock en
  // unidades. Ver `enUnidadesDelProducto`.
  const enLaCuenta = useMemo(
    () => loQueHayEnLaCuenta(enUnidadesDelProducto(carrito.lineas, deQuienEs)),
    [carrito.lineas, deQuienEs],
  );
  /*
    ═══════════════════════════════════════════════════════════════════════════
     «OCULTAR LOS AGOTADOS EN VENDER» (2026-10-07)
    ═══════════════════════════════════════════════════════════════════════════

    Lo pidió el dueño: que lo que no tiene existencia no salga. Desactivarlo
    no servía, porque no vuelve solo cuando llega la mercancía; esto sí: la
    existencia es la de esta sucursal y la caja se entera por las huellas.

    Siguen saliendo: lo que ya está en la cuenta (se ve qué se lleva) y lo que
    se escribió por su código exacto. El lector no pasa por aquí
    (`procesarCodigo` busca en el catálogo entero): si está en la mano, hay.
  */
  const { data: negocioDeLaCaja } = useQuery({
    queryKey: ["negocio"],
    queryFn: negocio.obtener,
    staleTime: 10 * 60 * 1000,
  });
  const ocultarAgotados = negocioDeLaCaja?.settings?.ocultar_agotados ?? false;
  const conExistencia = useMemo(() => {
    if (!ocultarAgotados) return items;
    const codigo = busqueda.trim();
    return items.filter(
      (i) =>
        !estaAgotado(i) ||
        enLaCuenta.has(i.id) ||
        (codigo !== "" && (i.barcode === codigo || i.sku === codigo)),
    );
  }, [items, ocultarAgotados, enLaCuenta, busqueda]);
  const ordenados = useMemo(
    () => primeroLoDeLaCuenta(conExistencia, enLaCuenta),
    [conExistencia, enLaCuenta],
  );

  const visibles = useMemo(() => ordenados.slice(0, cuantasPintar), [ordenados, cuantasPintar]);

  /*
    Lo que el buscador dice a su derecha, solo en la computadora (en el
    teléfono ese sitio es para la cámara, y no hay tecla Enter a la vista):

      · vacío → cuántos productos hay para vender (o en la categoría elegida).
      · escribiendo → cuántos coinciden, o «Sin resultados».
      · con UN solo resultado → «↵ Enter lo agrega». El atajo ya existía y no
        lo sabía nadie: quien teclea «coca» y ve una sola, pulsa Enter y listo.

    ⚠️ Aquí decía «Lector listo» con el cursor dentro, y se quitó: el dueño no
    tiene lector y le salía igual. Un aviso de un aparato que no está es ruido,
    y además no se puede saber desde el navegador si hay uno conectado.
  */
  const conCamara = seEscaneaConLaCamara();
  const unSoloResultado = !conCamara && busqueda.trim() !== "" && items.length === 1;
  const elUnico = unSoloResultado ? items[0]! : null;
  const enterLoAgrega =
    elUnico !== null &&
    !(elUnico.has_variants && elUnico.variant_count > 1) &&
    !sePreguntaCuanto(elUnico) &&
    !esDeMontoVariable(elUnico) &&
    !cajasPorProducto.has(elUnico.id);
  const faltanPorPintar = ordenados.length - visibles.length;
  const cuantosDice = items.length.toLocaleString("es-VE");

  // El centinela del final. Cuando entra en pantalla, se pide otra tanda.
  const finDeLaGrilla = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const nodo = finDeLaGrilla.current;
    if (!nodo || faltanPorPintar <= 0) return;

    const observador = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((e) => e.isIntersecting)) setCuantasPintar((n) => n + POR_TANDA);
      },
      // Se adelanta media pantalla para que la siguiente tanda ya esté puesta
      // cuando el dedo llegue abajo. Si se espera al borde exacto, se ve el
      // salto.
      { rootMargin: "400px" },
    );
    observador.observe(nodo);
    return () => observador.disconnect();
  }, [faltanPorPintar]);

  // Quiénes pueden prestar un servicio.
  const personal = useMemo(
    () => (miembros ?? []).filter((m) => m.status === "active" && m.can_provide_services),
    [miembros],
  );

  // Si el negocio es de UNA sola persona, no se pregunta quién atendió: la
  // respuesta es obvia y el backend la asigna solo. Sin datos del equipo (sin
  // internet) tampoco se pregunta: el servidor lo resolverá al sincronizar.
  const faltaPersonal = personal.length > 1 ? totales.faltaPersonal : [];

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ EL BUSCADOR RECUPERA EL FOCO. SIN ESTO EL LECTOR SE APAGA SOLO.
    ═══════════════════════════════════════════════════════════════════════════

    Un lector de código de barras es un teclado: escribe el código y pulsa
    Enter. Eso solo funciona si el foco está en el buscador — y en un mostrador
    de verdad el foco se pierde a los diez segundos, porque el cajero toca un
    producto de la grilla con el dedo. Ahí el foco se queda en ese botón, y el
    siguiente escaneo se escribe en la nada: no pasa nada, no hay error, el
    cajero pasa el producto otra vez y sigue sin pasar nada.

    Se enfocaba una sola vez al abrir la pantalla. Ahora se recupera cada vez
    que cambia el carrito, que es después de cada toque y de cada escaneo.

    ⚠️ Y NUNCA le quita el foco a algo que se está escribiendo. Desde que el
    cobro pasa en la columna de al lado, en esta misma pantalla hay casillas de
    importes, de referencia del Pago Móvil y del código de descuento. Robarles
    el foco a media cifra sería mucho peor que un escaneo perdido.
  */
  useEffect(() => {
    const activo = document.activeElement;
    const estaEscribiendo =
      activo instanceof HTMLInputElement ||
      activo instanceof HTMLTextAreaElement ||
      activo instanceof HTMLSelectElement;

    if (estaEscribiendo && activo !== buscadorRef.current) return;
    buscadorRef.current?.focus();
  }, [carrito.lineas.length]);

  /**
   * Enter en el buscador = lo que hace un lector de código de barras.
   *
   * Todo se resuelve en el dispositivo: coincidencia exacta por código o SKU
   * primero, y si no, el único resultado visible.
   */
  async function alEnviarBusqueda(e: React.FormEvent) {
    e.preventDefault();
    await procesarCodigo(busqueda.trim());
    teclado.guardarTeclado();
  }

  /**
   * Qué se hace con un código, venga de donde venga.
   *
   * ⚠️ Los tres caminos acaban aquí: el lector láser (que es un teclado y manda
   * un Enter), la cámara del teléfono, y quien lo teclea a mano. Tener uno solo
   * es lo que hace que la cámara herede sin escribir nada la etiqueta de la
   * balanza, el código de una variante y el viaje al servidor — que son
   * precisamente los tres casos que costó descubrir.
   */
  // El lector, aunque el buscador no tenga el cursor (se tocó un botón, se
  // abrió el menú): ver `lib/lectorGlobal.ts` (2026-10-03).
  useAlLeerCodigo((codigo) => void procesarCodigo(codigo));

  // «Vender» desde la consulta rápida de otra pantalla: el producto entra en
  // cuanto el catálogo de la caja está listo.
  const ubicacion = useLocation();
  const navegarAqui = useNavigate();
  const codigoQueTrajo = useRef<string | null>((ubicacion.state as { codigo?: string } | null)?.codigo ?? null);
  useEffect(() => {
    const codigo = codigoQueTrajo.current;
    if (!codigo || datos.items.length === 0) return;
    codigoQueTrajo.current = null;
    navegarAqui(ubicacion.pathname, { replace: true, state: null });
    void procesarCodigo(codigo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datos.items.length]);

  async function procesarCodigo(texto: string) {
    if (!texto) return;

    const exacto = datos.items.find((i) => i.barcode === texto || i.sku === texto);
    if (exacto) {
      // Un plato con variaciones, aunque se escanee, pregunta cuál.
      if ((exacto.variaciones?.length ?? 0) > 0) setEligiendoVariacion(exacto);
      else carrito.agregar(exacto);
      setBusqueda("");
      return;
    }

    /*
      ── Que lo resuelva el servidor ────────────────────────────────────────

      ⚠️ Se pregunta al SERVIDOR y solo cuando ya no se encontró en la copia
      local, en ese orden y no al revés. La búsqueda local resuelve el 99 % de
      los escaneos, sin red y al instante.

      Hay DOS códigos que la copia local no puede resolver, y los dos acaban
      aquí:

        · **La etiqueta de la balanza.** No existe en ningún producto: lleva el
          peso metido dentro y cambia con cada corte de queso.

        · **⚠️ EL CÓDIGO DE UNA VARIANTE**, que es el que rompía escaneos de
          verdad. Un producto con tallas o colores viaja al POS SIN código
          —`barcode = variante.barcode if not item.has_variants else None` en
          `catalog/service.py`— porque el suyo lo lleva cada variante. Así que
          escanear una camisa talla M no encontraba nada: ni en la copia local,
          porque no está, ni aquí, porque esta consulta solo se hacía con la
          balanza encendida y códigos de 12 a 14 dígitos.

          Una tienda de ropa con un lector no podía escanear NADA.

      Por eso ya no se pregunta solo por las etiquetas de balanza: se pregunta
      por cualquier código que no esté en la copia. El viaje al servidor solo
      pasa cuando la búsqueda local ya falló, que es justo cuando hace falta.
    */
    if (enLinea) {
      try {
        const r = await catalogo.buscarPorCodigo(texto);
        // ⚠️ Sin tallas, sin variante: igual que al tocarlo o al encontrarlo en
        // la copia local. Con el id de la variante la clave de la línea era
        // otra, y el mismo producto —recién creado, que aún no estaba en la
        // copia— quedaba en DOS líneas en vez de sumarse.
        // ⚠️ Sin tallas, sin variante; y el peso de la balanza, sumado. Ver
        // `comoEntraLoEscaneado`: los dos dejaban de sumar.
        const entra = comoEntraLoEscaneado(r);
        carrito.agregar(r.item, entra.variantId, entra.varianteNombre, entra.cantidad);

        setBusqueda("");
        // El peso que entró, venga de la etiqueta o sacado de su importe.
        aviso.exito(
          r.item.name,
          r.de_balanza && entra.cantidad
            ? `${formatQty(String(entra.cantidad))} ${r.item.unit ?? ""}`.trim()
            : r.variant.name || undefined,
        );
        return;
      } catch {
        // Sin conexión o código desconocido: se sigue al aviso de abajo, que
        // es lo que el cajero necesita leer.
      }
    }

    if (items.length === 1) {
      alTocarProducto(items[0]!);
      setBusqueda("");
      return;
    }

    aviso.error(
      "No se encontró ese código",
      items.length === 0 ? "No hay ningún producto que coincida." : "Elígelo de la lista.",
    );
  }

  /**
   * Un producto con tallas pregunta cuál; el resto entra directo.
   *
   * ⚠️ El lector de código de barras NO pasa por aquí cuando acierta de lleno:
   * un código de barras identifica una casilla concreta —esa camisa M negra
   * tiene el suyo— así que preguntar la talla después de haberla escaneado
   * sería preguntar algo que ya se sabe.
   */
  function alTocarProducto(item: ItemListRow, sinPreguntarLaCaja = false) {
    // Un plato con variaciones pregunta cuál: «Pollo, Carne o Mixta». Va antes
    // que las tallas porque sus variantes no son tallas (ver `ElegirVariacion`).
    if ((item.variaciones?.length ?? 0) > 0) {
      setEligiendoVariacion(item);
      return;
    }
    // Un plato con extras los pregunta antes de entrar: «+ Queso», «+ Tocineta».
    // Sin internet no hay de dónde leerlos: entra el plato solo, como antes.
    if (item.tiene_opciones && !item.has_variants && enLinea) {
      setPidiendoExtras({ item, variacion: null });
      return;
    }
    if (item.has_variants && item.variant_count > 1) {
      setEligiendoTalla(item);
      return;
    }

    /*
      ⚠️ CON CAJA O TIRA, SE PREGUNTA «¿SUELTO O CAJA?».

      Solo en los productos que el dueño configuró así; el resto del catálogo
      sigue entrando de un toque. Y el lector no pasa por aquí: el código de la
      caja es otro, y escaneándolo entra la caja directo.

      «Suelto» vuelve a entrar aquí con `sinPreguntarLaCaja`, para seguir el
      camino de siempre: si se vende al peso, pregunta cuánto.
    */
    if (!sinPreguntarLaCaja && cajasPorProducto.has(item.id)) {
      setEligiendoCaja(item);
      return;
    }

    /*
      ⚠️ LO QUE SE VENDE AL PESO PREGUNTA CUÁNTO.

      Antes entraba como UN KILO, que casi nunca es lo que alguien lleva. Para
      vender 400 gramos había que meterlo y después corregir la cantidad a 0,4
      —dividir de cabeza, con gente esperando—, y para «dame dos dólares de
      queso» no había forma: esa cuenta la hacía el cajero a mano.

      Ver `BarraDePeso`, la franja que aparece debajo del buscador y pregunta
      las dos cosas a la vez sin abrir ninguna ventana.
    */
    if (sePreguntaCuanto(item)) {
      setPesando(item);
      return;
    }

    /*
      ⚠️ UNA RECARGA PREGUNTA DE CUÁNTO ES.

      Con precios fijos, el bodegón que cobra «50% de lo que sea» tenía que
      crear un producto por cada monto —«Recarga 100», «Recarga 500»— y aun así
      nunca estaban todos: llegaba alguien pidiendo 137 y no había botón.

      Ver `BarraDeRecarga`: el cajero escribe lo que le pidieron y la franja le
      contesta en grande lo que tiene que cobrar.
    */
    if (esDeMontoVariable(item)) {
      setRecargando(item);
      return;
    }

    carrito.agregar(item);
    limpiarTrasAgregar();
  }

  /**
   * Después de agregar desde la grilla, el buscador queda vacío.
   *
   * Lo pidió el dueño: se escribe «coca», se toca la Coca-Cola, y el buscador
   * se limpia solo. Quien teclea a mano porque no tiene lector busca un
   * producto detrás de otro, y borrar lo escrito cada vez es un paso de más
   * con la cola esperando. Además, con el buscador vacío vuelve la grilla
   * entera, con lo de la cuenta arriba.
   *
   * ⚠️ Solo el texto: la categoría elegida se queda. Quien está vendiendo
   * «bebidas» sigue en bebidas.
   */
  function limpiarTrasAgregar() {
    setBusqueda("");
  }

  function alCobrar() {
    if (faltaPersonal.length > 0) {
      aviso.error(
        "Falta indicar quién atendió",
        `${faltaPersonal.map((l) => l.nombre).join(", ")}`,
      );
      setCarritoAbierto(true);
      return;
    }
    // La tasa, fresca justo antes de cobrar. Ver `refrescarTasas`.
    void datos.refrescarTasas();
    setPagoAbierto(true);
  }

  function alVenderConExito(venta: Sale) {
    setPagoAbierto(false);
    setCarritoAbierto(false);
    // ⚠️ ANTES de vaciar: lo que se resta es lo que había en la cuenta. Es lo
    // que hace que la esquina de cada tarjeta diga 9 después de vender tres de
    // doce, con internet o sin él. Ver `restarLoVendido`.
    //
    // ⚠️ Salvo al CORREGIR una venta: ahí la original devolvió su mercancía al
    // anularse, y restar solo lo nuevo dejaría la esquina mintiendo (16 donde
    // hay 18). Se vuelve a pedir el catálogo, que ya trae la cuenta buena.
    if (venta.corrige_a_id) void datos.refrescar();
    else datos.descontarVendido(enUnidadesDelProducto(carrito.lineas, deQuienEs));
    carrito.vaciar();
    setVentaHecha(venta);
    void qc.invalidateQueries({ queryKey: ["ventas-hoy"] });
    void qc.invalidateQueries({ queryKey: ["resumen-hoy"] });
    void qc.invalidateQueries({ queryKey: ["caja-actual"] });
    buscadorRef.current?.focus();
  }

  /*
    El apartado editado ya se guardó (2026-10-26): la mercancía de la versión
    vieja volvió y salió la nueva, así que el catálogo se vuelve a pedir, como
    al corregir. Y de vuelta a Apartados con su detalle abierto.
  */
  function alGuardarApartado(apartado: { id: string }) {
    setPagoAbierto(false);
    setCarritoAbierto(false);
    void datos.refrescar();
    carrito.vaciar();
    navegarAqui(`/apartados?ver=${apartado.id}`);
  }

  const hayCarrito = carrito.lineas.length > 0;

  /*
    ⚠️ «Usar impuestos en mi negocio» TIENE QUE APAGAR ESTO TAMBIÉN.

    Ese ajuste promete, con estas palabras, que «Softfi no menciona impuestos en
    ninguna pantalla». Un interruptor de IGTF en el resumen del pedido de un
    negocio que lo tiene apagado rompe la promesa — y es justo la clase de cosa
    que hace dudar de todos los demás ajustes.
  */
  const { data: ajustesDelNegocio } = useQuery({
    queryKey: ["negocio"],
    queryFn: negocio.obtener,
    staleTime: 10 * 60 * 1000,
  });
  const usaImpuestos = ajustesDelNegocio?.settings?.taxes_enabled ?? false;

  /*
    Los impuestos que el cajero puede encender en esta venta: el IVA que se
    suma, el IGTF, y los que cada negocio se haya creado.

    ⚠️ Esto SÍ necesita internet, y a propósito no se guarda en el dispositivo.
    Sin señal no aparecen los interruptores y la venta se cobra sin ellos, que
    es exactamente lo que pasaba antes de que existieran. Meterlos en la copia
    local costaría una versión nueva de la base del navegador — y con ella el
    riesgo de dejar sin vender a quien esté a medias — para una función que se
    usa en una de cada veinte ventas.
  */
  const { data: impuestosCargados } = useQuery({
    queryKey: ["impuestos", "pedido"],
    queryFn: () => impuestos.listar(true, "pedido"),
    enabled: usaImpuestos,
    // Un impuesto no cambia dos veces al día: no hay que volver a pedirlo cada
    // vez que el cajero entra en la pantalla.
    staleTime: 10 * 60 * 1000,
  });

  /*
    ⚠️ El apagado se comprueba OTRA VEZ aquí, y no sobra.

    `enabled: false` deja de pedirlos, pero NO tira lo que ya se había pedido:
    si el dueño apaga los impuestos con la pantalla de vender abierta en otra
    pestaña, los interruptores seguirían ahí hasta que la caché caduque sola.
    Un ajuste tiene que hacer efecto cuando se toca, no cuando le toque.
  */
  const impuestosDePedido = usaImpuestos ? (impuestosCargados ?? []) : [];

  return (
    <div
      /*
        ⚠️ LA TRANSICION VA EN `style` Y NO EN UNA CLASE.

        Lo que se anima es `grid-template-columns`, y Tailwind no tiene utilidad
        para transicionar esa propiedad concreta. `transition-all` la cogeria de
        paso, pero tambien animaria todo lo demas de este contenedor — y este
        contenedor envuelve el catalogo entero.
      */
      style={{ transition: "grid-template-columns 320ms cubic-bezier(0.22, 1, 0.36, 1)" }}
      className={cx(
        "lg:grid lg:gap-4 xl:gap-5",
        // El carrito crece con la pantalla, pero MUY poco a propósito.
        //
        // Lo que tiene dentro son renglones de texto —nombre, cantidad,
        // precio— y esos no mejoran por ser más anchos. Todo lo que se le dé
        // de más al carrito se le quita a la rejilla, que sí lo aprovecha:
        // cada 190 px de más ahí es una columna entera de productos.
        //
        // 20rem en un portátil, 22rem en un monitor grande. El resto, para los
        // productos.
        cobrandoEnPc
          ? // ── COBRANDO ──
            //
            // El cobro casi dobla su ancho. No es capricho: ahí caben dos
            // monedas a la vez, el vuelto, los datos del Pago Móvil y cuatro
            // atajos. A 20rem todo eso vivía apretado y partido en dos líneas.
            //
            // ⚠️ Y lo que se le quita NO es espacio útil: se le quita al
            // resumen del pedido, que son renglones de texto —nombre, cantidad,
            // precio— y esos no mejoran por ser más anchos. Es la misma razón
            // por la que el carrito crece tan poco cuando no se está cobrando.
            //
            // ⚠️ Y en un monitor grande, más todavía (2026-09-25): con Cashea
            // el cobro lleva la inicial, dos filas y seis datos, y a 36rem
            // había que bajar una pantalla para confirmar mientras el resumen
            // de la izquierda tenía medio metro vacío.
            "lg:grid-cols-[minmax(0,1fr)_32rem] xl:grid-cols-[minmax(0,1fr)_38rem] 2xl:grid-cols-[minmax(0,1fr)_44rem]"
          : "lg:grid-cols-[minmax(0,1fr)_20rem] xl:grid-cols-[minmax(0,1fr)_22rem]",
      )}
    >
      {/* ═══════════════ CATÁLOGO ═══════════════ */}
      {/*
        ⚠️ `min-w-0` NO ES DECORACIÓN: SIN ÉL LA PANTALLA SE ROMPE CON MUCHOS
        PRODUCTOS.

        Un `1fr` de CSS Grid es en realidad `minmax(auto, 1fr)`, y ese `auto`
        significa «nunca más estrecho que mi contenido». Con 41 categorías, la
        fila de categorías mide 4.533 px de ancho mínimo — y aunque ella sí sabe
        desplazarse sola, ese mínimo se lo contagia a la columna entera.

        Resultado medido con el archivo real de 3.440 productos: la columna
        pasaba de 689 px a 4.533, las tarjetas salían de 1.126 px cada una, y el
        carrito se iba fuera de la pantalla. El dueño lo describió como «la GUI
        hace muchísimas filas y el carrito se va muy a la derecha y se pierde».

        `minmax(0,1fr)` arriba y `min-w-0` aquí dicen lo mismo por los dos lados:
        esta columna puede ser tan estrecha como haga falta, y lo que no quepa
        que se desplace por dentro.
      */}
      {/*
        LOS DOS OCUPAN LA MISMA CELDA, uno encima del otro.

        Es lo que permite que uno se vaya deslizando mientras el otro entra. Con
        un `pagoAbierto ? A : B` normal, el catalogo desapareceria de golpe y no
        habria nada que animar.

        ⚠️ Y el catalogo NO SE DESMONTA, solo se aparta. Desmontarlo perderia el
        desplazamiento y volver de un cobro cancelado dejaria al cajero arriba
        del todo, buscando otra vez lo que ya tenia delante.
      */}
      <div className="grid min-w-0 [&>*]:col-start-1 [&>*]:row-start-1">
      <div
        hidden={catalogoOculto}
        aria-hidden={cobrandoEnPc}
        className={cx(
          "@container min-w-0 space-y-3",
          "transition-[opacity,transform] duration-300 ease-out",
          cobrandoEnPc && "pointer-events-none -translate-x-6 opacity-0",
        )}
      >
        {/*
          Corrigiendo una venta: se dice arriba, bien visible, y se puede dejar.
          Sin esto el cajero cobraría creyendo que hace una venta nueva — y en
          realidad anularía la original.
        */}
        {/* Editando un apartado (2026-10-26): lo mismo, bien visible. */}
        {carrito.editandoApartado && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius-control)] border border-accent/40 bg-accent-soft px-3.5 py-2.5">
            <p className="text-sm text-ink">
              <strong className="text-accent">Editando el apartado {carrito.editandoApartado.numero}.</strong>{" "}
              Agregue o quite lo que haga falta: lo que ya pagó se mantiene.
            </p>
            <Boton tamano="sm" variante="secundario" onClick={carrito.vaciar}>
              Dejar de editar
            </Boton>
          </div>
        )}
        {carrito.corrigiendo && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius-control)] border border-accent/40 bg-accent-soft px-3.5 py-2.5">
            <p className="text-sm text-ink">
              <strong className="text-accent">Corrigiendo la venta {carrito.corrigiendo.numero}.</strong>{" "}
              Al cobrar, la original se anula y queda esta.
            </p>
            <Boton tamano="sm" variante="secundario" onClick={carrito.vaciar}>
              Dejar de corregir
            </Boton>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="mr-auto titulo-pagina">Vender</h1>

          <div className="flex flex-wrap items-center gap-2">
            {/*
              ⚠️ Solo si de verdad se está usando la copia: sin internet, o
              porque no se pudo bajar la nueva. Salía también en el segundo
              en que la copia se pinta mientras llega la del servidor, y con
              su wifi tachado parecía que se había ido la conexión.
            */}
            {datos.desdeCache && !datos.renovando && (
              <Insignia tono="aviso">
                <WifiOff size={11} /> Catálogo guardado
              </Insignia>
            )}
            {/*
              La tasa, en su esquina y con su nombre (2026-10-02). Era una
              pastilla gris «1 USD = 853,4993 VES» que nadie leía; el dueño:
              «la tasa BCV no se puede ver como tal en ningún lado».
            */}
            <TasaDelDia monedas={datos.monedas} />
          </div>
          <BotonDeAyuda />
        </div>

        {/* Aviso de caja cerrada (solo si sabemos que está cerrada) */}
        {enLinea && turnoCaja === null && (
          <Nota tono="aviso" icono={<AlertTriangle size={15} />}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                La caja está cerrada. Puedes vender igual, pero estas ventas{" "}
                <strong>no entrarán en la cuenta</strong> del turno.
              </span>
              <Link
                to="/caja"
                className="font-semibold underline underline-offset-2 whitespace-nowrap"
              >
                Abrir caja
              </Link>
            </div>
          </Nota>
        )}

        {/*
          ═══════════════════════════════════════════════════════════════════
           EL BUSCADOR
          ═══════════════════════════════════════════════════════════════════

          La pieza más usada de toda la aplicación: cada venta entra por aquí,
          tecleando, con el lector o con la cámara. Por eso no es un campo más
          del formulario:

            · Más alto (h-14) y con la letra más grande: se escribe de pie,
              con prisa, a veces con el teléfono en una mano.
            · Un recuadro sólido del color de la marca a la izquierda. Es lo
              que se reconoce de lejos como «aquí se busca», y lo que lo hace
              nuestro y no el de cualquier otro sistema.
            · Con el cursor dentro, el borde y un halo del mismo color: el
              cajero rápido va con el teclado y tiene que ver dónde está.
            · A la derecha, lo que está encontrando. Ver `conCamara` arriba.

          Nada se mueve solo: cambia al escribir y al entrar, y ya.
        */}
        <form onSubmit={alEnviarBusqueda} className="group relative" data-tour="buscador">
          {/*
            ═══════════════════════════════════════════════════════════════════
             ⚠️ ESTE BOTÓN INVISIBLE ES LO QUE MANTIENE VIVO EL LECTOR
            ═══════════════════════════════════════════════════════════════════

            Un lector de código de barras es un teclado: escribe el código y
            manda un Enter. Ese Enter solo envía el formulario si el navegador
            hace «envío implícito», y eso solo pasa cuando el formulario tiene un
            botón de envío **o un único campo de texto**.

            Hoy funciona por lo segundo: aquí solo hay un campo. Es decir,
            funciona de casualidad — el día que alguien añada otra casilla a esta
            barra, el Enter deja de enviar y el lector se muere. Sin error, sin
            aviso: el cajero pasa el producto y no pasa nada.

            Con este botón ya no depende de eso. No se ve, no se puede tabular
            hasta él, y está para que la regla del navegador se cumpla siempre.
          */}
          <button type="submit" className="hidden" tabIndex={-1} aria-hidden />
          <span
            className={cx(
              "pointer-events-none absolute left-2 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center",
              "rounded-[calc(var(--radius-control)-1px)] bg-accent text-on-accent",
              // Un filo más oscuro abajo: le da cuerpo de tecla sin sombra.
              "shadow-[inset_0_-2px_0_0_rgb(0_0_0/0.14)]",
            )}
            aria-hidden
          >
            <Search size={19} strokeWidth={2.4} />
          </span>
          {/*
            ⚠️ Un `<input>` propio y no `Campo`: aquí cambian el alto, el borde,
            el halo y la sombra, y `Campo` ya trae los suyos. Dos clases de
            alto en el mismo elemento no se pisan en orden: gana la que Tailwind
            escriba después en la hoja, que no tiene por qué ser la de aquí.
          */}
          <input
            ref={buscadorRef}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            inputMode={teclado.inputMode}
            onPointerDown={teclado.onPointerDown}
            onBlur={teclado.onBlur}
            placeholder={conCamara ? "Busca o escanea un producto…" : "Busca por nombre o código…"}
            aria-label="Buscar un producto"
            className={cx(
              "block h-14 w-full rounded-[var(--radius-card)] border border-line-strong bg-surface",
              "pl-[3.75rem] text-[15px] font-medium text-ink placeholder:font-normal placeholder:text-faint",
              "shadow-[var(--shadow-card)] transition-[border-color,box-shadow]",
              "hover:border-accent/50",
              "focus:border-accent focus:outline-none focus:ring-4 focus:ring-accent/15",
              // El sitio de lo que va a la derecha, para que un nombre largo no
              // se meta por debajo: la cámara y la X en el teléfono; el
              // contador, el Enter, la X y el teléfono (2026-11-02) en la
              // computadora.
              //
              // ⚠️ Con `@[34rem]:` y no `sm:`: mide la columna del catálogo,
              // no la ventana (ver la grilla, más abajo). Con el carrito al
              // lado, en una pantalla de 1.040 px a esta barra le quedan 275:
              // el contador se comía la mitad y el «Busca por nombre o
              // código…» salía cortado. Si no cabe, no sale.
              conCamara
                ? busqueda
                  ? "pr-24"
                  : "pr-14"
                : unSoloResultado
                  ? "pr-14 @[34rem]:pr-56"
                  : busqueda
                    ? "pr-24 @[34rem]:pr-48"
                    : "pr-14 @[34rem]:pr-44",
            )}
            autoComplete="off"
            // No usamos type="search" para que el lector de código de barras
            // (que envía Enter al final) dispare el submit del formulario.
          />
          {/*
            ⚠️ LA CÁMARA VA A LA DERECHA DEL TODO, Y LA X SE APARTA.

            Cuando hay texto escrito conviven las dos. La cámara se queda en el
            borde —es donde llega el pulgar con el teléfono en la mano— y la X
            se corre a su izquierda. Al revés, la X aparecería y desaparecería
            debajo del dedo justo donde estaba la cámara, y se tocaría la que no
            era en mitad de una venta.
          */}
          <BotonDeEscaner
            onClick={() => setEscaneando(true)}
            className="right-2.5"
            titulo="Escanear un producto con la cámara"
          />

          {/* Lo que va encontrando. Solo en la computadora. */}
          {!conCamara && (
            <span
              className={cx(
                "pointer-events-none absolute top-1/2 hidden -translate-y-1/2 items-center gap-1.5 text-xs @[34rem]:flex",
                // A la izquierda del botón del teléfono, y de la X si la hay.
                busqueda ? "right-[5.5rem]" : "right-12",
              )}
              aria-live="polite"
            >
              {unSoloResultado ? (
                <>
                  <kbd className="inline-flex items-center gap-1 rounded-md border border-b-2 border-line-strong bg-surface-2 px-1.5 py-0.5 font-sans text-[11px] font-semibold text-ink">
                    <CornerDownLeft size={11} strokeWidth={2.5} />
                    Enter
                  </kbd>
                  <span className="text-muted">{enterLoAgrega ? "lo agrega" : "lo elige"}</span>
                </>
              ) : busqueda && items.length === 0 ? (
                <span className="font-medium text-danger">Sin resultados</span>
              ) : (
                <span className="rounded-full bg-surface-2 px-2 py-0.5 text-muted">
                  <span className="tabular font-semibold text-ink-soft">{cuantosDice}</span>{" "}
                  {busqueda
                    ? items.length === 1
                      ? "resultado"
                      : "resultados"
                    : items.length === 1
                      ? "producto"
                      : "productos"}
                </span>
              )}
            </span>
          )}

          {busqueda && (
            <button
              type="button"
              onClick={() => {
                setBusqueda("");
                buscadorRef.current?.focus();
              }}
              aria-label="Limpiar"
              className={cx(
                "absolute top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full text-muted transition-colors hover:bg-surface-hover hover:text-ink",
                // A la izquierda de la cámara o del teléfono, que van al borde.
                "right-12",
              )}
            >
              <X size={16} />
            </button>
          )}
        </form>

        {/*
          La cámara, a pantalla completa.

          ⚠️ `continuo`: no se cierra al leer. Un cajero sin lector láser pasa
          los productos de la compra uno detrás de otro, y cada cierre serían un
          par de segundos de cámara encendiéndose otra vez. Lo que lee entra por
          el mismo sitio que el lector láser — `procesarCodigo`.
        */}
        <EscanerDeCodigo
          abierto={escaneando}
          onCerrar={() => setEscaneando(false)}
          onLeer={(codigo) => void procesarCodigo(codigo)}
          continuo
          titulo="Pasa los productos"
        />

        {/*
          ⚠️ LA FRANJA DEL PESO VA AQUÍ, EN EL FLUJO, Y NO ES UNA VENTANA.

          Antes esto abría un cuadro encima. El dueño puso el dedo en el
          problema con un ejemplo real: una charcutería despacha «500 de queso
          amarillo, 500 de queso para rallar, 500 de jamón, un kilo de carne» de
          corrido — con un cuadro eso son cuatro ventanas abriéndose y
          cerrándose, y en tablet cuatro veces el teclado subiendo.

          Aquí abajo del buscador no tapa nada, no oscurece y no se cierra al
          agregar: se toca el siguiente producto y solo cambia de nombre.
        */}
        {pesando && (
          <BarraDePeso
            item={pesando}
            formato={formato}
            onAgregar={(cantidad) => {
              carrito.agregar(pesando, null, null, cantidad);
              limpiarTrasAgregar();
            }}
            onCerrar={() => setPesando(null)}
          />
        )}

        {/*
          La misma franja, para «¿de cuánto es la recarga?».

          Se pinta en el mismo sitio y nunca las dos a la vez: un servicio de
          monto variable no se vende al peso.
        */}
        {recargando && (
          <BarraDeRecarga
            item={recargando}
            formato={formato}
            onAgregar={(monto) => {
              carrito.agregar(recargando, null, null, 1, monto);
              limpiarTrasAgregar();
              // Para que ese monto sea un botón la próxima vez. Ver `recargas.ts`.
              recordarMonto(recargando.id, monto);
            }}
            onCerrar={() => setRecargando(null)}
          />
        )}

        {/*
          Las carritos guardados van aquí arriba, no escondidas en un menú: el
          cajero tiene que ver de un vistazo que hay un cliente esperando. Si
          no hay ninguna, el bloque no existe y no ocupa sitio.
        */}
        <ListaDeCarritos items={datos.items} impuestosDePedido={impuestosDePedido} />

        {/* ⚠️ Solo en catálogos gigantes. Sin esto, lo que no cupo simplemente
            no aparecía, y parecía que Softfi había perdido productos. */}
        {datos.catalogoRecortado && (
          <Nota tono="aviso" icono={<AlertTriangle size={15} />}>
            Tu catálogo tiene más de {TOPE_PRODUCTOS.toLocaleString("es")} productos y en este
            aparato se cargaron los primeros {TOPE_PRODUCTOS.toLocaleString("es")}. Los demás se
            venden escaneando su código, con internet.
          </Nota>
        )}

        {/* Categorías */}
        {categorias.length > 0 && (
          <div className="scroll-fino -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
            <BotonCategoria
              activo={categoria === ""}
              onClick={() => {
                setCategoria("");
                setSubcategoria("");
              }}
              nombre="Todo"
            />
            {categorias.map((c) => (
              <BotonCategoria
                key={c.id}
                activo={categoria === c.id}
                onClick={() => {
                  setCategoria(c.id);
                  setSubcategoria("");
                }}
                nombre={c.nombre}
                color={c.color}
              />
            ))}
          </div>
        )}

        {/*
          Las subcategorías de la elegida: «Placas madres» → «AMD», «Intel».
          «Todas» enseña la familia entera, que es lo que se ve al tocar la
          madre. Solo sale si la madre tiene alguna.
        */}
        {subcategorias.length > 0 && (
          <div className="scroll-fino -mx-4 -mt-1 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0">
            <BotonCategoria
              activo={subcategoria === ""}
              onClick={() => setSubcategoria("")}
              nombre="Todas"
            />
            {subcategorias.map((c) => (
              <BotonCategoria
                key={c.id}
                activo={subcategoria === c.id}
                onClick={() => setSubcategoria(c.id)}
                nombre={c.nombre}
                color={c.color}
              />
            ))}
          </div>
        )}

        {/* Grilla */}
        {datos.cargando ? (
          <Esqueleto filas={4} />
        ) : datos.sinDatos ? (
          <Vacio
            icono={<WifiOff size={24} />}
            titulo="No hay catálogo guardado en este dispositivo"
            descripcion="Conéctate a internet una vez para descargar tu catálogo. Después podrás vender aunque se caiga la señal."
            accion={
              <Boton
                icono={<RefreshCw size={16} />}
                onClick={() => void datos.refrescar()}
                cargando={datos.cargando}
              >
                Intentar descargar
              </Boton>
            }
          />
        ) : items.length === 0 ? (
          <Vacio
            icono={busqueda ? <SearchX size={24} /> : <Package size={24} />}
            titulo={busqueda ? "Sin resultados" : "No hay nada para vender"}
            descripcion={
              busqueda
                ? "Prueba con otro nombre o código."
                : "Carga productos y servicios en el catálogo para poder vender."
            }
          />
        ) : (
          <div
            data-tour="grilla"
            /*
              ⚠️ `auto-fill` Y NO `grid-cols-N` CON PUNTOS DE CORTE.

              Los puntos de corte de Tailwind (`sm:`, `xl:`…) miran el ancho de
              la VENTANA, no el que le queda a esta columna. Y aquí no se parecen:
              en una pantalla de 1.440 px, con el menú lateral y el carrito, a la
              grilla le quedan 689. `xl:grid-cols-4` se activaba por los 1.440 y
              metía cuatro columnas en 689 px — tarjetas de 164 px donde no cabe
              un nombre.

              Con `auto-fill` manda el hueco de verdad: caben las que caben, y en
              un celular o con el menú desplegado se reacomoda solo.
            */
            className={cx(
              "grid gap-3 pb-32 lg:pb-4",
              // ═══════════════════════════════════════════════════════════
              //  ⚠️ SE MIDE EL HUECO, NO LA VENTANA (`@container`)
              // ═══════════════════════════════════════════════════════════
              //
              // Los puntos de corte normales de Tailwind (`sm:`, `xl:`) miran
              // el ancho de la PANTALLA. Y aquí eso miente por dos motivos a
              // la vez: está la barra lateral, y está el carrito. En una
              // pantalla de 1.280 px a la rejilla le quedan 608.
              //
              // Peor todavía: la barra se puede recoger. Con la misma pantalla
              // el hueco cambia de 608 a 800, y unos puntos de corte por
              // ventana no se enteran — por eso al dueño le salieron SIETE
              // columnas al recogerla.
              //
              // `@container` mide la columna misma. Lo que decide es el sitio
              // que hay, que es lo único que importa.
              //
              // ⚠️ Y EL TOPE ES CUATRO, A PROPÓSITO.
              //
              // Una rejilla no mejora por meter más cosas. Con seis columnas
              // el dueño lo dijo claro: «hay demasiada info compacta y se ve
              // feo». Pasadas las cuatro, cada columna de más son nombres
              // cortados y objetivos más pequeños de tocar con el dedo.
              //
              // En un monitor grande el espacio de sobra va a hacer las
              // tarjetas MÁS GRANDES, no a meter más. Eso es lo que se hace
              // con el espacio: respirar.
              "grid-cols-2",           // teléfono
              "@[42rem]:grid-cols-3",  // tableta, o un portátil con la barra recogida
              "@[58rem]:grid-cols-4",  // monitor. Y de aquí no pasa.
            )}
          >
            {visibles.map((item) => (
              <BotonProducto
                key={item.id}
                item={item}
                formato={formato}
                secundarias={secundarias}
                enLaCuenta={enLaCuenta.get(item.id)}
                cajas={cajasPorProducto.get(item.id)}
                onClick={() => alTocarProducto(item)}
              />
            ))}
          </div>
        )}

        {/*
          El final de la grilla: centinela Y botón a la vez.

          El observador carga la siguiente tanda solo con bajar, que es lo que
          espera cualquiera. Pero es un botón de verdad, no un cartel, por dos
          razones: quien prefiera tocar puede, y si el observador no corre —hay
          navegadores viejos y vistas empotradas donde no llega a dispararse— la
          pantalla no se queda con sesenta productos y sin salida.

          Una función que solo tiene un camino automático es una función que un
          día no está.
        */}
        {faltanPorPintar > 0 && (
          <div ref={finDeLaGrilla} className="pb-32 pt-2 text-center lg:pb-4">
            <button
              type="button"
              onClick={() => setCuantasPintar((n) => n + POR_TANDA)}
              className="rounded-[var(--radius-control)] border border-line-strong px-4 py-2 text-xs font-medium text-muted transition-colors hover:text-ink"
            >
              Ver más · quedan {faltanPorPintar.toLocaleString("es-VE")}
            </button>
            <p className="mt-1.5 text-[11px] text-faint">
              O busca por nombre o código para llegar antes.
            </p>
          </div>
        )}
      </div>

        {/* ═══════════════ RESUMEN DEL PEDIDO ═══════════════ */}
        {/*
          ⚠️ `hidden lg:block` AUNQUE `cobrandoEnPc` YA LO DECIDA.

          Son dos cierres para la misma puerta, y el segundo hace falta: esta
          celda de la rejilla SÍ se dibuja en el celular —es donde va el
          catálogo— así que si algún día `cobrandoEnPc` se volviera cierto ahí
          por cualquier camino, el resumen aparecería encima del catálogo con
          la barra de «Ver la cuenta» tapándolo por abajo. La regla de CSS lo
          impide pase lo que pase con el estado.
        */}
        {cobrandoEnPc && (
          <div className="hidden lg:block">
          <ResumenDelPedido
            totales={totales}
            formato={formato}
            secundarias={secundarias}
            codigos={codigosPorProducto}
            personal={personal}
            impuestosDePedido={impuestosDePedido}
          />
          </div>
        )}
      </div>

      {/* ═══════════════ CARRITO — PC ═══════════════ */}
      {/*
        ═════════════════════════════════════════════════════════════════════
         ⚠️ EL COBRO PASA AQUÍ, EN LA COLUMNA. NO ABRE NINGUNA VENTANA.
        ═════════════════════════════════════════════════════════════════════

        Antes se abría un modal encima de todo, y eso tapaba el catálogo. Lo
        que pasa entonces es lo de siempre en un mostrador: el cliente dice
        «ah, y dame un chicle» con la ventana de cobrar abierta, y hay que
        cancelar, agregarlo, y volver a empezar el cobro.

        Con el cobro en la columna, el catálogo se queda al lado. Se agrega el
        chicle y el total se actualiza solo.

        ⚠️ `!carritoAbierto` NO ES DECORACIÓN: sin eso el cobro se monta DOS
        VECES.

        Esta columna es `hidden lg:block` —el navegador la esconde en el
        celular, pero React la dibuja igual— y la hoja de abajo dibuja otra. Con
        el carrito eso daba lo mismo: son los mismos datos pintados dos veces.
        Con el COBRO no: son dos pantallas de pago vivas, cada una con sus
        pagos, su vuelto y su petición de venta. Se toca «Otros métodos» en una
        y la otra no se entera.

        `carritoAbierto` solo puede ser verdadero en el celular, porque el botón
        que lo abre es `lg:hidden`. Así que las dos condiciones se excluyen
        solas y siempre hay exactamente un cobro montado.
      */}
      <aside className="hidden lg:block" data-tour="carrito">
        <div className="sticky top-6">
          {pagoAbierto && !carritoAbierto && carrito.editandoApartado ? (
            <Tarjeta>
              <GuardarApartado
                totales={totales}
                formato={formato}
                impuestosDePedido={impuestosDePedido}
                onCerrar={() => setPagoAbierto(false)}
                onGuardado={alGuardarApartado}
              />
            </Tarjeta>
          ) : pagoAbierto && !carritoAbierto ? (
            <Tarjeta>
              <ModalPago
                comoPanel
                totales={totales}
                monedas={datos.monedas}
                metodos={datos.metodos}
                impuestosDePedido={impuestosDePedido}
                textoVolver="Volver al catálogo"
                onCerrar={() => setPagoAbierto(false)}
                onVendido={alVenderConExito}
              />
            </Tarjeta>
          ) : (
            <PanelCarrito
              totales={totales}
              formato={formato}
              secundarias={secundarias}
              codigos={codigosPorProducto}
              personal={personal}
              faltaPersonal={faltaPersonal}
              onCobrar={alCobrar}
              onCotizacionHecha={setCotizacionHecha}
            />
          )}
        </div>
      </aside>

      {/* ═══════════════ CARRITO — CELULAR ═══════════════ */}
      {hayCarrito && (
        <>
          {/* Barra fija abajo, encima de la navegación.
              ⚠️ La navegación mide 3,5rem MÁS la franja del iPhone (la raya
              de abajo para salir de la app). Con `bottom-14` a secas, en un
              iPhone esta barra se montaba encima de las pestañas y tapaba la
              mitad de cada una. Lo encontró la auditoría. */}
          <button
            type="button"
            onClick={() => setCarritoAbierto(true)}
            className="fixed inset-x-0 bottom-[calc(3.5rem+env(safe-area-inset-bottom,0px))] z-40 flex items-center gap-3 border-t border-line bg-accent px-4 py-3 text-on-accent shadow-[var(--shadow-pop)] lg:hidden"
          >
            <div className="relative">
              <ShoppingCart size={20} />
              {/* size-5, no size-4.5: Tailwind no genera fracciones más allá de 3.5 */}
              <span className="absolute -right-2 -top-2 flex size-5 items-center justify-center rounded-full bg-surface text-[10px] font-bold text-accent">
                {totales.cantidadUnidades}
              </span>
            </div>
            <span className="flex-1 text-left text-sm font-medium">Ver la cuenta</span>
            <span className="tabular text-lg font-semibold">
              {formatMoney(totales.total, formato)}
            </span>
            <ChevronUp size={18} />
          </button>

          {/* Hoja desplegable con el carrito completo */}
          {carritoAbierto && (
            <div className="fixed inset-0 z-50 flex items-end lg:hidden">
              <div
                className="absolute inset-0 bg-black/50"
                onClick={() => setCarritoAbierto(false)}
                aria-hidden
              />
              <div className="animar-entrada relative max-h-[88dvh] w-full overflow-hidden rounded-t-3xl bg-surface">
                <div className="flex items-center justify-between border-b border-line px-4 py-3">
                  {/* Cobrando, el título lo pone el propio panel —con su
                      «volver a la cuenta» al lado—, así que la hoja calla para
                      no decir «Cobrar» dos veces seguidas. */}
                  <p className="font-semibold text-ink">{pagoAbierto ? "" : "La cuenta"}</p>
                  <button
                    type="button"
                    onClick={() => {
                      setPagoAbierto(false);
                      setCarritoAbierto(false);
                    }}
                    aria-label="Cerrar"
                    className="rounded-lg p-2 text-muted hover:text-ink"
                  >
                    <X size={18} />
                  </button>
                </div>
                <div className="scroll-fino max-h-[74dvh] overflow-y-auto p-3 pb-safe">
                  {/*
                    En el celular la hoja ES la pantalla, así que el cobro pasa
                    dentro de ella y no encima. Es la misma idea que en el PC:
                    el cobro ocurre donde ya estaba la cuenta, sin abrir otra
                    capa que tape lo de atrás.
                  */}
                  {pagoAbierto && carrito.editandoApartado ? (
                    <GuardarApartado
                      totales={totales}
                      formato={formato}
                      impuestosDePedido={impuestosDePedido}
                      onCerrar={() => setPagoAbierto(false)}
                      onGuardado={alGuardarApartado}
                    />
                  ) : pagoAbierto ? (
                    <ModalPago
                      comoPanel
                      totales={totales}
                      monedas={datos.monedas}
                      metodos={datos.metodos}
                      impuestosDePedido={impuestosDePedido}
                      onCerrar={() => setPagoAbierto(false)}
                      onVendido={alVenderConExito}
                    />
                  ) : (
                    <PanelCarrito
                      totales={totales}
                      formato={formato}
                      secundarias={secundarias}
                      codigos={codigosPorProducto}
                      personal={personal}
                      faltaPersonal={faltaPersonal}
                      onCobrar={alCobrar}
                      onCotizacionHecha={setCotizacionHecha}
                      sinBorde
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════════════ MODALES ═══════════════ */}
      {eligiendoVariacion && (
        <ElegirVariacion
          item={eligiendoVariacion}
          formato={formato}
          onElegir={(v) => {
            // Los extras, después de la variación y en su propia ventana.
            if (eligiendoVariacion.tiene_opciones && enLinea) {
              setPidiendoExtras({ item: eligiendoVariacion, variacion: v });
            } else {
              carrito.agregar(eligiendoVariacion, v.id, v.nombre);
              limpiarTrasAgregar();
            }
            setEligiendoVariacion(null);
          }}
          onCerrar={() => setEligiendoVariacion(null)}
        />
      )}

      {pidiendoExtras && (
        <ModalDeOpciones
          itemId={pidiendoExtras.item.id}
          nombre={
            pidiendoExtras.variacion
              ? `${pidiendoExtras.item.name} · ${pidiendoExtras.variacion.nombre}`
              : pidiendoExtras.item.name
          }
          precioBase={
            pidiendoExtras.variacion
              ? precioConImpuesto(pidiendoExtras.item, pidiendoExtras.variacion.precio)
              : (pidiendoExtras.item.price_with_tax ?? pidiendoExtras.item.base_price)
          }
          formato={formato}
          accion="Agregar"
          descripcion="Seleccione los extras del plato, si los lleva."
          quitables={
            quitarIngredientesActivo(negocioDeLaCaja?.settings)
              ? quitablesDelPlato(pidiendoExtras.item, pidiendoExtras.variacion?.id ?? null)
              : []
          }
          onCerrar={() => setPidiendoExtras(null)}
          onApuntar={(_ids, extras, sin) => {
            const { item, variacion } = pidiendoExtras;
            carrito.agregar(
              item,
              variacion?.id ?? null,
              variacion?.nombre ?? null,
              undefined,
              undefined,
              extras,
              sin,
            );
            limpiarTrasAgregar();
            setPidiendoExtras(null);
          }}
        />
      )}

      {eligiendoTalla && (
        <SelectorDeTalla
          item={eligiendoTalla}
          locationId={sucursalParaVender(tenant?.id)}
          onElegir={(variantId, nombre) => {
            carrito.agregar(eligiendoTalla, variantId, nombre);
            limpiarTrasAgregar();
            // Se cierra al elegir. Dejarlo abierto para «llevo dos tallas»
            // suena bien y en la práctica hace que se quede abierto cuando ya
            // terminaste, tapando el carrito con la cola esperando.
            setEligiendoTalla(null);
          }}
          onCerrar={() => setEligiendoTalla(null)}
        />
      )}


      {eligiendoCaja && (
        <SelectorDePresentacion
          producto={eligiendoCaja}
          presentaciones={cajasPorProducto.get(eligiendoCaja.id) ?? []}
          formato={formato}
          onSuelto={() => {
            const producto = eligiendoCaja;
            setEligiendoCaja(null);
            alTocarProducto(producto, true);
          }}
          onCaja={(caja) => {
            carrito.agregar(caja);
            setEligiendoCaja(null);
            limpiarTrasAgregar();
          }}
          onCerrar={() => setEligiendoCaja(null)}
        />
      )}

      {ventaHecha && (
        <ModalRecibo venta={ventaHecha} onCerrar={() => setVentaHecha(null)} recienCreada />
      )}

      {/*
        El papel de la cotización, en el momento de hacerla. Trae ya los tres
        tamaños de impresión, «Enviar» por WhatsApp y «Pasar a venta» — que es
        lo que hace falta si el cliente dice que sí ahí mismo.
      */}
      {cotizacionHecha && (
        <ModalCotizacion
          cotizacion={cotizacionHecha}
          recienCreada
          onCerrar={() => setCotizacionHecha(null)}
        />
      )}
    </div>
  );
}

// =============================================================================
//  Botón de categoría
// =============================================================================

function BotonCategoria({
  activo,
  onClick,
  nombre,
  color,
}: {
  activo: boolean;
  onClick: () => void;
  nombre: string;
  color?: string | null;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        // Compactas a propósito: un negocio con cuarenta categorías las tiene
        // todas en esta fila, y cada píxel de más es un arrastre más para
        // llegar a la última.
        "flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
        activo
          ? "border-accent bg-accent-soft text-accent"
          : "border-line-strong text-muted hover:text-ink",
      )}
    >
      {color && (
        <span className="size-2 rounded-full" style={{ backgroundColor: color }} aria-hidden />
      )}
      {nombre}
    </button>
  );
}

// =============================================================================
//  Botón de producto de la grilla
// =============================================================================

function BotonProducto({
  item,
  formato,
  secundarias,
  enLaCuenta,
  cajas,
  onClick,
}: {
  item: ItemListRow;
  formato: ReturnType<typeof comoFormato>;
  /** Las demás monedas en que se cobra, todas. */
  secundarias: import("@/lib/types").TenantCurrency[];
  /** Si está en la cuenta, y cuánto. Vacío = no está. */
  enLaCuenta?: EnLaCuenta;
  /** Su caja, su tira… Vacío en casi todo el catálogo. */
  cajas?: ItemListRow[];
  onClick: () => void;
}) {
  const esServicio = item.kind === "service";
  /*
    ⚠️ «SEGÚN EL MONTO» NO TIENE PRECIO QUE ENSEÑAR.

    Lo pone el monto que pida el cliente, en la franja que se abre al tocarla.
    La tarjeta decía «$0,00», que se lee como «es gratis» — justo lo contrario.
  */
  const segunElMonto = esDeMontoVariable(item);

  // El color propio del producto manda sobre el de su categoría.
  const color = item.color ?? item.category_color ?? null;

  const enCuenta = enLaCuenta !== undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={enCuenta ? `${item.name}. En la cuenta: ${cuantoDice(enLaCuenta)}` : undefined}
      className={cx(
        // ⚠️ `justify-start`, NO `justify-between`.
        //
        // En una rejilla todas las tarjetas de una fila miden lo mismo: lo que
        // mida la del nombre más largo. Con `justify-between`, ese alto de más
        // se metía COMO HUECO ENTRE EL NOMBRE Y EL PRECIO, y quedaba una fila
        // de tarjetas donde los precios bailaban a distintas alturas.
        //
        // ⚠️ El alto y el relleno van con el ANCHO de la tarjeta. Con el tope
        // en cuatro columnas, una tarjeta mide ~300 px: una tarjeta grande
        // necesita margen grande, o no parece grande — parece mal hecha.
        "group relative flex min-h-[5.75rem] flex-col justify-start overflow-hidden text-left",
        "rounded-[var(--radius-card)] border p-3.5",
        "transition-all duration-150",
        // El levantamiento al pasar el ratón es de 1 px. Más se vuelve saltarín
        // en una grilla de 40 productos; menos no se percibe.
        "hover:-translate-y-px hover:shadow-[var(--shadow-raised)]",
        "active:translate-y-0 active:scale-[0.98]",
        /*
          ⚠️ EN LA CUENTA: VERDE, EL DE «DINERO QUE ENTRA», Y VIVO.

          Lo pidió el dueño: que lo que ya lleva el cliente se vea de un vistazo,
          sin mirar a la otra columna. La primera versión usaba el verde suave
          de los avisos y la vio «bastante plana» en modo claro: sobre la grilla
          blanca casi no se distinguía. Ahora tiene su propio verde (`in-cart`
          en `index.css`), con borde que se ve y una sombra verde.
        */
        enCuenta
          ? "border-in-cart/80 bg-in-cart-soft shadow-[var(--shadow-in-cart)] ring-1 ring-inset ring-in-cart/30 hover:border-in-cart"
          : "border-line bg-surface shadow-[var(--shadow-card)] hover:border-accent/60",
      )}
    >
      {/*
        La franja de color de la categoría, arriba y de lado a lado. Funciona
        como la pestaña de una carpeta: se reconoce el grupo sin leer nada.
      */}
      {color && (
        <span
          className="absolute inset-x-0 top-0 h-1"
          style={{ backgroundColor: color }}
          aria-hidden
        />
      )}

      {enCuenta ? (
        /* Cuántos lleva: «✓ 2», «✓ 1,5 kg». Donde antes salía el «+». */
        <span
          className="tabular absolute right-2 top-2 inline-flex items-center gap-0.5 rounded-full bg-in-cart py-0.5 pl-1 pr-1.5 text-[11px] font-bold leading-none text-on-in-cart shadow-[0_1px_2px_0_rgb(0_0_0/0.12)]"
          aria-hidden
        >
          <Check size={12} strokeWidth={3} />
          {cuantoDice(enLaCuenta)}
        </span>
      ) : (
        /* El «+» que aparece al pasar el ratón: dice qué va a ocurrir al tocar.
           En pantalla táctil no molesta porque no hay estado de hover. */
        <span
          className={cx(
            "pointer-events-none absolute right-2 top-2 flex size-6 items-center justify-center",
            "rounded-full bg-accent text-on-accent opacity-0 transition-opacity",
            "group-hover:opacity-100",
          )}
          aria-hidden
        >
          <Plus size={14} />
        </span>
      )}

      <div className={cx("flex w-full items-start gap-2", color && "mt-1")}>
        <Monograma item={item} color={color} esServicio={esServicio} />
        {/*
          Dos líneas. A 300 px de ancho caben unos 60 caracteres en dos
          renglones — de sobra para «003105 REPUESTOS P/BORRADOR RETRACTIL FABER
          CASTELL». Con tres, las tarjetas de nombre largo estiraban su fila.
        */}
        <span
          className={cx(
            "line-clamp-2 text-[13px] font-medium leading-snug text-ink",
            // La píldora de la cuenta es más ancha que el «+»: el nombre le deja
            // su sitio en vez de meterse por debajo.
            enCuenta ? "pr-14" : "pr-5",
          )}
        >
          {item.name}
        </span>
      </div>

      {/*
        El SKU y el código de barras (2026-10-12), en su propio renglón y de
        lado a lado de la tarjeta. Debajo del nombre, junto al monograma, no
        cabían los dos: salían «FIJ-ABR-… · 75912340000…», cortados ambos.
      */}
      <CodigosDelProducto sku={item.sku} barcode={item.barcode} className="mt-1.5 w-full" />

      {/*
        Que se sepa ANTES de tocarla que también va por caja: si no, el
        «¿suelto o caja?» sale de sorpresa. Una marca pequeña, en el color de
        la marca, con los nombres que puso el dueño.
      */}
      {/* Igual con las variaciones: que se sepa que va a preguntar cuál. */}
      {(item.variaciones?.length ?? 0) > 0 && (
        <span className="mt-1.5 inline-flex max-w-full items-center gap-1 self-start rounded-full bg-accent-soft px-1.5 py-0.5 text-[10px] font-semibold text-accent">
          <Layers size={11} className="shrink-0" aria-hidden />
          <span className="truncate">
            {item.variaciones!.map((v) => v.nombre).join(" · ")}
          </span>
        </span>
      )}

      {cajas && cajas.length > 0 && (
        <span className="mt-1.5 inline-flex max-w-full items-center gap-1 self-start rounded-full bg-accent-soft px-1.5 py-0.5 text-[10px] font-semibold text-accent">
          <Boxes size={11} className="shrink-0" aria-hidden />
          <span className="truncate">
            {cajas.map((c) => c.presentacion_nombre).join(" · ")}
          </span>
        </span>
      )}

      {/*
        Abajo: el precio a la izquierda y cuánto queda a la derecha.

        ⚠️ `flex-wrap` con la esquina en `ml-auto`: en un teléfono, con un
        precio largo en bolívares, las dos cosas no caben en la misma línea.
        Así el stock baja a la siguiente, pegado a la derecha, en vez de
        montarse encima del precio o cortarlo.
      */}
      <div className="mt-2 flex w-full flex-wrap items-end gap-x-2 gap-y-1.5">
        <div className="min-w-0">
        {/*
          El precio es lo más grande de la tarjeta, por encima del nombre.
          En un mostrador la pregunta es "¿cuánto cuesta?".
        */}
        {segunElMonto ? (
          <p className="whitespace-nowrap text-[13px] font-semibold leading-none text-ink-soft">
            Según el monto
          </p>
        ) : (
          <p className="tabular whitespace-nowrap text-[19px] font-semibold leading-none tracking-[-0.02em] text-ink">
            {/* Con variaciones, el del plato es el de la más económica. */}
            {(item.variaciones?.length ?? 0) > 1 && (
              <span className="mr-1 align-baseline text-[11px] font-medium tracking-normal text-muted">
                desde
              </span>
            )}
            {formatMoney(item.price_with_tax, formato)}
          </p>
        )}
        {!segunElMonto && (
          <Equivalencias
            monto={item.price_with_tax}
            monedas={secundarias}
            principal={formato}
            className={cx(
              "mt-1.5 text-[12px]",
              // Sobre el verde, el gris de siempre se queda corto de contraste.
              enCuenta ? "text-ink-soft" : "text-muted",
            )}
          />
        )}
        </div>

        <Existencia item={item} enLaCuenta={enLaCuenta?.cantidad ?? 0} enCuenta={enCuenta} />
      </div>
    </button>
  );
}

/**
 * Cuánto queda, en la esquina de abajo a la derecha de la tarjeta.
 *
 * Lo pidió el dueño. Sin llamar la atención cuando hay (gris, pequeño) y en
 * color solo cuando hay algo que hacer: ámbar al llegar al mínimo, rojo si en
 * la cuenta hay más de lo que queda o si está agotado. Ver `existencia.ts`.
 */
function Existencia({
  item,
  enLaCuenta,
  enCuenta,
}: {
  item: ItemListRow;
  enLaCuenta: number;
  enCuenta: boolean;
}) {
  const estado = estadoDeExistencia(item, enLaCuenta);
  if (estado === null) return null;

  const texto = estado === "agotado" ? "Agotado" : cuantoHay(item);
  const ESTILO: Record<EstadoDeExistencia, string> = {
    // Sobre el verde de la cuenta, el gris de siempre se queda corto.
    hay: enCuenta ? "text-ink-soft" : "text-muted",
    pocos: "bg-warning-soft text-warning ring-1 ring-inset ring-warning/30",
    "no-alcanza": "bg-danger-soft text-danger ring-1 ring-inset ring-danger/30",
    agotado: "bg-danger-soft text-danger ring-1 ring-inset ring-danger/30",
  };
  const DICE: Record<EstadoDeExistencia, string> = {
    hay: `Quedan ${texto}`,
    pocos: `Quedan pocos: ${texto}`,
    "no-alcanza": `Solo quedan ${texto}, y en la cuenta hay más`,
    agotado: "Agotado",
  };

  return (
    <span
      className={cx(
        "ml-auto inline-flex shrink-0 items-baseline gap-1 whitespace-nowrap rounded-full text-[11px] leading-none",
        estado === "hay" ? "py-0.5" : "px-1.5 py-1 font-semibold",
        ESTILO[estado],
      )}
      title={DICE[estado]}
      aria-label={DICE[estado]}
    >
      {estado !== "agotado" && (
        <span className={cx("font-medium", estado === "hay" && "opacity-80")}>Stock</span>
      )}
      <span className={cx("tabular", estado === "hay" && "font-semibold")}>{texto}</span>
    </span>
  );
}

/**
 * El recuadro de la tarjeta: la foto del producto si la tiene, y si no, sus
 * iniciales en el color de su categoría.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ ANTES ERA UNA CAJITA, IGUAL EN LOS TRES MIL PRODUCTOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * No decía nada —era la misma en todos— y es la que usa cualquier otro
 * sistema. Lo dijo el dueño: había uno casi idéntico. Las iniciales SÍ
 * distinguen: en una fila de bolígrafos, «BP» se encuentra antes de leer.
 * Ver `iniciales` en `catalogoConCuenta.ts`.
 *
 * Los servicios, en redondo: se distinguen de un producto sin leer nada, que
 * era lo que hacían las tijeritas.
 *
 * ⚠️ La foto se cae a las iniciales si no carga, en vez de dejar un hueco roto:
 * sin internet, las fotos que no estén guardadas no llegan.
 */
function Monograma({
  item,
  color,
  esServicio,
}: {
  item: ItemListRow;
  color: string | null;
  esServicio: boolean;
}) {
  const [sinFoto, setSinFoto] = useState(false);
  const tinte = tinteDeCategoria(color);
  const conFoto = Boolean(item.image_url) && !sinFoto;

  return (
    <span
      className={cx(
        "flex size-7 shrink-0 items-center justify-center overflow-hidden border text-[10px] font-bold tracking-wide",
        esServicio ? "rounded-full" : "rounded-[0.5rem]",
        tinte ? "text-ink" : "border-accent/25 bg-accent-soft text-accent",
      )}
      style={tinte ?? undefined}
      aria-hidden
    >
      {conFoto ? (
        <img
          src={item.image_url!}
          alt=""
          loading="lazy"
          className="size-full object-cover"
          onError={() => setSinFoto(true)}
        />
      ) : (
        iniciales(item.name)
      )}
    </span>
  );
}

// =============================================================================
//  Resumen del pedido — lo que ocupa el sitio del catálogo al cobrar
// =============================================================================

/**
 * Lo que se está cobrando, mientras se cobra.
 *
 * ══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ EL CATÁLOGO SE APARTA Y NO SE QUEDA
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Antes el catálogo se quedaba al lado del cobro, para poder agregar el chicle
 * que el cliente pide a última hora. La idea era buena y el reparto del espacio
 * era malo: el cobro —dos monedas, el vuelto, los datos del Pago Móvil, los
 * atajos— vivía en veinte rem mientras al lado había una rejilla que en ese
 * momento nadie estaba mirando.
 *
 * Ahora el catálogo se aparta y su sitio lo ocupa esto, que es lo que SÍ se
 * mira al cobrar: qué se lleva, cuánto es y qué impuestos entran.
 *
 * ⚠️ Y NO SE PIERDE LO QUE SE GANÓ ENTONCES. Las líneas siguen siendo las de
 * verdad, con sus botones de más y menos: el chicle de última hora se sigue
 * pudiendo ajustar sin cancelar el cobro, y «Volver al catálogo» devuelve la
 * rejilla — con los pagos ya escritos intactos.
 */
function ResumenDelPedido({
  totales,
  formato,
  secundarias,
  codigos,
  personal,
  impuestosDePedido,
}: {
  totales: ReturnType<typeof calcularTotales>;
  formato: ReturnType<typeof comoFormato>;
  secundarias: import("@/lib/types").TenantCurrency[];
  codigos: CodigosPorProducto;
  personal: import("@/lib/types").Member[];
  impuestosDePedido: Tax[];
}) {
  const carrito = useCarrito();
  const { puede } = useAuth();
  const [eligiendoCliente, setEligiendoCliente] = useState(false);

  /*
    ⚠️ EL IVA ENCENDIDO SE SUMA TAMBIÉN AQUÍ, NO SOLO EN EL COBRO.

    El dueño: «solo cambia el precio de la derecha, queremos que también cambie
    el de la izquierda». Tenía razón: el IVA no depende de cómo se pague — va
    sobre la venta entera, menos lo exento —, así que se sabe desde ya y no hay
    motivo para que el total de aquí diga otra cosa que el del cobro.

    Lo que NO se suma aquí es el IGTF y parecidos: esos dependen de en qué
    moneda pague el cliente, y eso solo se sabe en el cobro.

    La cuenta es la misma del cobro y del servidor: `recargoDeImpuestosDePedido`.
  */
  const iva = impuestosDePedido
    .filter((t) => carrito.impuestosPedido.includes(t.id) && t.currency_codes.length === 0)
    .map((t) => ({
      impuesto: t,
      monto: recargoDeImpuestosDePedido({
        encendidos: [t],
        total: totales.total,
        entradoPorMoneda: {},
        exentoPorImpuesto: totales.exentoPorImpuesto,
        sumaDeLineas: totales.sumaDeLineas,
      }),
    }));
  const totalConIva = redondearMonto(totales.total + iva.reduce((n, x) => n + x.monto, 0));
  const hayDeMoneda = impuestosDePedido.some(
    (t) => carrito.impuestosPedido.includes(t.id) && t.currency_codes.length > 0,
  );

  return (
    <div className="animar-desde-izquierda min-w-0 space-y-3">
      {/*
        ⚠️ AQUÍ NO VA UN «VOLVER». Lo tiene el cobro, a la derecha.

        Estuvo un rato en los dos sitios y se lea mal: dos salidas con dos
        nombres distintos —«Volver al catálogo» y «Volver a la cuenta»— que
        hacían exactamente lo mismo. El cajero mira a la derecha, que es donde
        está trabajando, así que la salida vive ahí.
      */}
      <h1 className="titulo-pagina">Resumen del pedido</h1>

      <Tarjeta>
        <div className="divide-y divide-line">
          {carrito.lineas.map((linea) => (
            <FilaCarrito
              key={linea.key}
              linea={linea}
              formato={formato}
              personal={personal}
              puedeDescuento={puede("pos.discount")}
              codigos={codigosDeLaLinea(linea, codigos)}
            />
          ))}
        </div>

        <div className="border-t border-line bg-surface-2 px-4 py-3">
          {/*
            DE QUIÉN ES LA VENTA, también mientras se cobra.

            Antes, al empezar a cobrar, la columna del carrito se convertía en el
            cobro y esta fila desaparecía con ella: el cajero descubría que le
            hacía falta el cliente cuando ya no tenía dónde ponerlo. Ahora el
            resumen se queda a la vista y la fila con él.
          */}
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className="text-xs text-muted">Cliente</span>
            <PastillaDeCliente
              cliente={
                carrito.clienteId && carrito.clienteNombre
                  ? { id: carrito.clienteId, nombre: carrito.clienteNombre }
                  : null
              }
              alPulsar={() => setEligiendoCliente(true)}
              alQuitar={() => carrito.ponerCliente(null, null)}
            />
          </div>

          <SelectorDeCliente
            abierto={eligiendoCliente}
            cerrar={() => setEligiendoCliente(false)}
            alElegir={(c) => {
              carrito.ponerCliente(c.id, c.name);
              // ⚠️ Y con él, SU lista de precios — igual que en la cuenta.
              //
              // Si aquí no se hiciera, poner el cliente antes de cobrar le
              // aplicaría sus precios y ponerlo DURANTE el cobro no. El mismo
              // cliente pagaría dos precios distintos según en qué momento se
              // acordaran de escribirlo.
              if (c.lista_precios_id) {
                carrito.ponerListaDePrecios(c.lista_precios_id, c.lista_precios_nombre ?? null);
              }
            }}
          />

          <div className="space-y-1 text-sm">
            <div className="flex justify-between text-muted">
              <span>Subtotal</span>
              <span className="tabular">{formatMoney(totales.subtotal, formato)}</span>
            </div>
            {totales.descuentoTotal > 0 && (
              <div className="flex justify-between text-muted">
                <span>Descuentos</span>
                <span className="tabular">−{formatMoney(totales.descuentoTotal, formato)}</span>
              </div>
            )}
            {totales.impuestoTotal > 0 && (
              <div className="flex justify-between text-muted">
                <span>Impuesto</span>
                <span className="tabular">{formatMoney(totales.impuestoTotal, formato)}</span>
              </div>
            )}
          </div>

          {/*
            ══════════════════════════════════════════════════════════════
             LOS IMPUESTOS QUE SE ENCIENDEN A MANO
            ══════════════════════════════════════════════════════════════

            ⚠️ APAGADOS POR DEFECTO, SIEMPRE. Activar el IGTF en Ajustes hace que
            aparezca AQUÍ, no que se cobre. Es la diferencia entre «este negocio
            puede cobrar IGTF» y «este negocio cobra IGTF en todo» — y la
            segunda no la puede decidir un ajuste, porque depende de con qué
            pague cada cliente.

            El monto no se pinta aquí a propósito: sale del cobro, que está a la
            derecha, en cuanto se escribe con qué se paga. Poner un número aquí
            antes de saberlo sería inventarlo.
          */}
          <InterruptoresDeImpuesto
            className="mt-3"
            impuestos={impuestosDePedido}
            encendidos={carrito.impuestosPedido}
            alAlternar={carrito.alternarImpuestoDePedido}
            exentos={exentosComoDinero(totales.exentoPorImpuesto, formato)}
          />

          <div className="mt-3 rounded-[var(--radius-control)] bg-surface p-3 ring-1 ring-line">
            {iva.map((x) => (
              <div key={x.impuesto.id} className="mb-2 flex justify-between text-sm text-muted">
                <span>
                  {x.impuesto.name} <span className="tabular">{x.impuesto.rate_percent}%</span>
                </span>
                <span className="tabular">{formatMoney(x.monto, formato)}</span>
              </div>
            ))}
            <div className="flex items-end justify-between gap-3">
              <span className="rotulo pb-1">Total</span>
              <div className="min-w-0 text-right">
                <p className="tabular truncate text-[28px] font-semibold leading-none tracking-[-0.03em] text-ink">
                  {formatMoney(totalConIva, formato)}
                </p>
                <Equivalencias
                  monto={totalConIva}
                  monedas={secundarias}
                  principal={formato}
                  enPila
                  className="mt-1.5 text-[13px] text-muted"
                />
              </div>
            </div>
          </div>

          {/*
            ⚠️ El total de arriba es el del CARRITO, sin los impuestos que se
            acaban de encender. Lo que se cobra de verdad —con el recargo ya
            dentro— lo canta el botón de cobrar, a la derecha.

            Se dice en voz alta y no se esconde: dos totales en pantalla sin
            explicar cuál es cuál es peor que uno solo.
          */}
          {hayDeMoneda && (
            <p className="mt-2 text-center text-xs text-muted">
              El IGTF se suma al cobrar, según en qué moneda pague.
            </p>
          )}
        </div>
      </Tarjeta>
    </div>
  );
}


// =============================================================================
//  Panel del carrito
// =============================================================================

function PanelCarrito({
  totales,
  formato,
  secundarias,
  codigos,
  personal,
  faltaPersonal,
  onCobrar,
  onCotizacionHecha,
  sinBorde = false,
}: {
  totales: ReturnType<typeof calcularTotales>;
  formato: ReturnType<typeof comoFormato>;
  /** Las demás monedas en que se cobra, todas. */
  secundarias: import("@/lib/types").TenantCurrency[];
  codigos: CodigosPorProducto;
  /** Quiénes pueden prestar servicios. Si es una sola persona, no se pregunta. */
  personal: import("@/lib/types").Member[];
  faltaPersonal: LineaCarrito[];
  onCobrar: () => void;
  /** La cotización recién guardada sube hasta `PosPage`, que enseña el papel. */
  onCotizacionHecha: (cotizacion: import("@/lib/types").Cotizacion) => void;
  sinBorde?: boolean;
}) {
  const carrito = useCarrito();
  const { puede } = useAuth();
  const vacio = carrito.lineas.length === 0;
  const [eligiendoCliente, setEligiendoCliente] = useState(false);

  // Las listas de precios activas, para el selector de arriba. Si el negocio no
  // tiene ninguna, el selector no aparece y la pantalla queda igual que antes.
  const { data: listasActivas } = useQuery({
    queryKey: ["listas-de-precios", "activas"],
    queryFn: () => listasDePrecios.listar(true),
  });

  return (
    <div
      className={cx(
        "flex max-h-[calc(100dvh-3rem)] flex-col overflow-hidden rounded-[var(--radius-card)] bg-surface",
        /*
          ⚠️ EL CARRITO SE DIFERENCIA DEL RESTO, A PROPÓSITO.

          Lo pidieron los clientes del dueño: «que el carrito se diferencie un
          poco más de lo demás de la pantalla, en colores». Tenían razón, y en
          el tema oscuro se notaba más: la cuadrícula de productos y el carrito
          son las dos tarjetas de la pantalla, las dos del mismo color, y el
          ojo no sabía cuál es la que manda.

          El color viene del acento del tema —ni un color escrito a mano—, así
          que funciona igual en claro (acento oscuro sobre fondo pálido) que en
          oscuro (acento claro sobre fondo profundo).
        */
        !sinBorde && "border border-accent/25 shadow-[var(--shadow-raised)]",
      )}
    >
      {/* Encabezado */}
      <div className="flex items-center justify-between gap-2 border-b border-accent/20 bg-accent-soft px-4 py-3">
        <p className="flex items-center gap-2 font-semibold text-accent">
          <ShoppingCart size={17} />
          La cuenta
          {/*
            La cuenta de unidades va en acento SÓLIDO y no en la insignia de
            siempre: la insignia se pinta sobre el mismo fondo que ahora tiene
            el encabezado, así que ahí desaparecía.
          */}
          {!vacio && (
            <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-bold text-on-accent tabular">
              {totales.cantidadUnidades}
            </span>
          )}
        </p>
        {!vacio && (
          <button
            type="button"
            onClick={carrito.vaciar}
            className="rounded-lg px-2 py-1 text-xs text-ink-soft transition-colors hover:bg-danger-soft hover:text-danger"
          >
            Vaciar
          </button>
        )}
      </div>

      {/*
        ═══════════════════════════════════════════════════════════════════════
         DE QUIÉN ES LA VENTA — ARRIBA DEL TODO, DEBAJO DEL TÍTULO
        ═══════════════════════════════════════════════════════════════════════

        Estaba abajo, pegado a los totales, y ahí llegaba tarde dos veces:

          · **No se veía con el carrito vacío.** Todo el bloque de totales vive
            dentro de un «si hay líneas», así que quien quería identificar al
            cliente ANTES de empezar —lo normal cuando la venta es a crédito, o
            cuando el negocio pide los datos siempre— no tenía dónde.

          · **Y al final se decide el pago, no de quién es la venta.** Para
            cuando el cajero llega a los totales ya está pensando en cobrar.

        El dueño lo pidió señalando el sitio: debajo de «La cuenta». Que es
        donde empieza la venta, no donde termina.

        Sigue siendo opcional. En una bodega la mayoría de las ventas son
        anónimas y pedirlo en cada una haría el punto de venta inservible: solo
        se vuelve obligatorio al fiar, porque una deuda sin dueño no se cobra.
      */}
      <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
        <span className="text-xs text-muted">Cliente</span>
        <PastillaDeCliente
          cliente={
            carrito.clienteId && carrito.clienteNombre
              ? { id: carrito.clienteId, nombre: carrito.clienteNombre }
              : null
          }
          alPulsar={() => setEligiendoCliente(true)}
          alQuitar={() => carrito.ponerCliente(null, null)}
        />
      </div>

      <SelectorDeCliente
        abierto={eligiendoCliente}
        cerrar={() => setEligiendoCliente(false)}
        alElegir={(c) => {
          carrito.ponerCliente(c.id, c.name);
          // ⚠️ Y con él, SU lista de precios.
          //
          // Es lo que hace útiles las listas: sin esto el cajero tiene que
          // acordarse de tocar «Mayor» en cada venta de cada mayorista, y el
          // día que se le olvide le cobra de detal a alguien que compra todas
          // las semanas. Eso no da ningún error — sale una venta normal por más
          // dinero del acordado, y quien lo descubre es el cliente.
          if (c.lista_precios_id) {
            carrito.ponerListaDePrecios(c.lista_precios_id, c.lista_precios_nombre ?? null);
          }
        }}
      />

      {/* Líneas */}
      <div className="scroll-fino min-h-0 flex-1 overflow-y-auto">
        {vacio ? (
          <div className="px-4 py-10 text-center">
            <ShoppingCart size={28} className="mx-auto mb-3 text-faint" />
            <p className="text-sm font-medium text-ink">Todavía no has puesto nada</p>
            <p className="mt-1 text-xs text-muted">
              Toca un producto o escanea su código de barras.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-line">
            {carrito.lineas.map((linea) => (
              <FilaCarrito
                key={linea.key}
                linea={linea}
                formato={formato}
                personal={personal}
                puedeDescuento={puede("pos.discount")}
                codigos={codigosDeLaLinea(linea, codigos)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Totales y cobro */}
      {!vacio && (
        <div className="border-t border-line bg-surface-2 px-4 py-3">
          {/*
            ═══════════════════════════════════════════════════════════════
             CON QUÉ PRECIOS SE COBRA
            ═══════════════════════════════════════════════════════════════

            Solo sale si el negocio tiene listas. Una fila de botones y no un
            desplegable: se toca sin leer, que es lo que hace falta con un
            cliente delante. (Por eso el tope de ocho listas.)

            ⚠️ Y se ve SIEMPRE cuál está puesta, incluso «Normal». Un selector
            que solo se nota cuando está cambiado es como se termina cobrando
            precio de mayor toda la tarde sin que nadie lo note: no da error,
            solo cobra de menos.
          */}
          {(listasActivas ?? []).length > 0 && (
            <div className="mb-3">
              <span className="text-xs text-muted">Precios</span>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => carrito.ponerListaDePrecios(null, null)}
                  className={cx(
                    "rounded-full border px-3 py-1 text-xs font-medium transition",
                    carrito.listaPreciosId === null
                      ? "border-accent bg-accent text-on-accent"
                      : "border-line text-muted hover:border-accent hover:text-ink",
                  )}
                >
                  Normal
                </button>
                {(listasActivas ?? []).map((l) => (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => carrito.ponerListaDePrecios(l.id, l.nombre)}
                    className={cx(
                      "rounded-full border px-3 py-1 text-xs font-medium transition",
                      carrito.listaPreciosId === l.id
                        ? "border-accent bg-accent text-on-accent"
                        : "border-line text-muted hover:border-accent hover:text-ink",
                    )}
                  >
                    {l.nombre}
                  </button>
                ))}
              </div>

              {/*
                ⚠️ Los totales de ESTA pantalla se calculan con el precio normal
                del catálogo — el precio de la lista lo resuelve el servidor al
                cobrar. Decirlo evita que alguien crea que la lista no se aplicó
                porque el total de arriba no cambió.
              */}
              {carrito.listaPreciosId && (
                <p className="mt-1.5 text-xs text-muted">
                  Se cobrará con <strong className="text-ink">{carrito.listaPreciosNombre}</strong>.
                  Los productos sin precio en esa lista van a su precio normal.
                </p>
              )}
            </div>
          )}

          {faltaPersonal.length > 0 && (
            <div className="mb-3">
              <Nota tono="aviso" icono={<User size={14} />}>
                Falta indicar quién atendió:{" "}
                <strong>{faltaPersonal.map((l) => l.nombre).join(", ")}</strong>
              </Nota>
            </div>
          )}

          <div className="space-y-1 text-sm">
            {totales.descuentoTotal > 0 && (
              <div className="flex justify-between text-muted">
                <span>Descuentos</span>
                <span className="tabular">−{formatMoney(totales.descuentoTotal, formato)}</span>
              </div>
            )}
            {totales.impuestoTotal > 0 && (
              <div className="flex justify-between text-muted">
                <span>Impuesto</span>
                <span className="tabular">{formatMoney(totales.impuestoTotal, formato)}</span>
              </div>
            )}
          </div>

          {/*
            EL TOTAL, tratado como el momento importante que es.

            Va en su propio bloque con fondo, no como una fila más de la lista.
            Es el número que el cajero canta en voz alta y que el cliente busca
            con la vista desde el otro lado del mostrador: tiene que encontrarse
            sin leer nada más.

            Y debajo, lo que es en cada una de las demás monedas, una por
            renglón — porque la segunda frase de cualquier cliente es "¿y en
            bolívares cuánto es?", o en pesos.
          */}
          <div className="mt-3 rounded-[var(--radius-control)] bg-surface p-3 ring-1 ring-line">
            <div className="flex items-end justify-between gap-3">
              <span className="rotulo pb-1">Total</span>
              <div className="min-w-0 text-right">
                <p className="tabular truncate text-[28px] font-semibold leading-none tracking-[-0.03em] text-ink">
                  {formatMoney(totales.total, formato)}
                </p>
                <Equivalencias
                  monto={totales.total}
                  monedas={secundarias}
                  principal={formato}
                  enPila
                  className="mt-1.5 text-[13px] text-muted"
                />
              </div>
            </div>
          </div>

          <Boton ancho tamano="lg" className="mt-3 text-base font-semibold" onClick={onCobrar}>
            {carrito.editandoApartado
              ? `Guardar el apartado ${carrito.editandoApartado.numero}`
              : `Cobrar ${formatMoney(totales.total, formato)}`}
          </Boton>

          {/*
            "Guardar carrito" va DEBAJO de Cobrar y en secundario: se usa una vez
            cada veinte ventas. Ponerlo al mismo nivel que el botón principal
            haría dudar al cajero cada vez, y el cajero tiene cola.
          */}
          <div className="mt-2">
            <BotonGuardarCarrito />
          </div>

          {/*
            ═══════════════════════════════════════════════════════════════════
             «HACER COTIZACIÓN» — LA TERCERA COSA QUE SE HACE CON UN CARRITO
            ═══════════════════════════════════════════════════════════════════

            Cobrarlo, dejarlo en espera, o escribirlo en un papel para que el
            cliente lo compare. Los tres parten del mismo pedido ya armado, y
            por eso el botón vive aquí y no en una pantalla aparte: una segunda
            pantalla para cotizar sería un segundo buscador de productos y un
            segundo carrito, que se desincronizan a la primera corrección.

            Va el ÚLTIMO y en secundario porque es lo que menos se hace. En una
            bodega, nunca; en una ferretería o un taller, todos los días — y en
            esos negocios se encuentra igual, porque quien cotiza lo busca.
          */}
          {/* Con su permiso detallado (2026-10-01): «Cotizaciones» dentro de vender. */}
          {puede("pos.cotizaciones") && (
            <div className="mt-2">
              <BotonHacerCotizacion onHecha={onCotizacionHecha} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
//  Una línea del carrito
// =============================================================================

function FilaCarrito({
  linea,
  formato,
  personal,
  puedeDescuento,
  codigos,
}: {
  linea: LineaCarrito;
  formato: ReturnType<typeof comoFormato>;
  personal: import("@/lib/types").Member[];
  puedeDescuento: boolean;
  /** Su SKU y su código de barras, si tiene. */
  codigos?: { sku: string | null; barcode: string | null } | null;
}) {
  const carrito = useCarrito();
  const limpiar = useLimpiarMonto();
  const [editandoDescuento, setEditandoDescuento] = useState(false);
  const [editandoSin, setEditandoSin] = useState(false);
  // «Sin tomate» (Funciones, 2026-10-27). La consulta del negocio ya está en
  // caché: la usa la caja entera.
  const { data: negocioDeLaFila } = useQuery({
    queryKey: ["negocio"],
    queryFn: negocio.obtener,
    staleTime: 10 * 60 * 1000,
  });
  const quitables =
    quitarIngredientesActivo(negocioDeLaFila?.settings) ? (linea.quitables ?? []) : [];
  const sin = linea.sin ?? [];

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ LA CASILLA DE CANTIDAD TIENE SU PROPIO BORRADOR
    ═══════════════════════════════════════════════════════════════════════════

    Antes cada tecla iba directo al carrito, y en el carrito 0 es «quitar la
    línea». Para escribir 0,25 kg de queso se empieza por «0»… y la línea
    desaparecía con la primera tecla. Lo mismo al borrar el 5 para poner un 8:
    la casilla quedaba vacía un instante y el producto se iba. Lo encontró la
    auditoría.

    Ahora lo que se escribe vive aquí mientras se escribe. Al carrito solo pasa
    una cantidad que sirve (mayor que 0); al salir de la casilla con algo que no
    sirve —vacío, 0—, vuelve la que había. Quitar se hace con la papelera o con
    el «−», que es donde se ve que se está quitando.

    `null` = no se está escribiendo: se enseña la del carrito.
  */
  const [borradorCantidad, setBorradorCantidad] = useState<string | null>(null);
  // Lo que se está escribiendo en el descuento, tal cual: ver su casilla.
  const [borradorDescuento, setBorradorDescuento] = useState<string | null>(null);
  const formaDeCantidad = linea.permiteDecimales ? /^\d*([.,]\d{0,3})?$/ : /^\d*$/;

  function escribirCantidad(texto: string) {
    // Lo que no es un número posible ni se deja escribir: «2.5» en algo que se
    // vende por piezas, letras, un segundo punto.
    if (!formaDeCantidad.test(texto)) return;
    setBorradorCantidad(texto);
    const n = Number.parseFloat(texto.replace(",", "."));
    if (Number.isFinite(n) && n > 0) carrito.cambiarCantidad(linea.key, n);
  }

  // ⚠️ La MISMA cuenta que el total (`desgloseDeLinea`), con la lista elegida.
  // Antes esta línea miraba solo el precio normal: con «Mayor» puesto, las
  // líneas decían una cosa y el total otra, y no sumaban.
  const { precioConImpuesto: brutoConImpuesto, total: lineaTotal } = desgloseDeLinea(
    linea,
    carrito.listaPreciosId,
  );
  const conLista = usaPrecioDeLista(linea, carrito.listaPreciosId);

  // `null` para todo lo que se vende por piezas, que es casi todo.
  const unidadMenuda = linea.permiteDecimales ? unidadDePeso(linea.unidad) : null;

  /*
    Cómo se dice el precio: «c/u» para lo que va de a uno, «por m» para lo que
    se corta y no tiene unidad chica.

    ⚠️ Sin esto, un cable a 1,20 el metro salía como «$1,20 c/u» con cantidad
    2,5 — o sea «dos coma cinco cada uno», que no significa nada. Y peor: hace
    dudar de si el precio es del metro o del rollo entero, que es justo lo que
    hay que comprobar de un vistazo antes de cobrar.
  */
  const porUnidadSuelta =
    !unidadMenuda && linea.permiteDecimales ? etiquetaDeUnidad(linea.unidad) : "";

  return (
    <div className="px-3 py-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-snug text-ink">
            {linea.nombre}
            {linea.varianteNombre && (
              <span className="text-muted"> · {linea.varianteNombre}</span>
            )}
            {/*
              ⚠️ DE CUÁNTO ERA LA RECARGA, PEGADO AL NOMBRE.

              El precio de la línea dice 750 y el monto es 500: son dos
              números distintos y sin este el cajero no puede comprobar que
              recargó lo que le pidieron. Y con dos recargas del mismo
              servicio en el carrito —una de 500 y otra de 1.000— sin esto
              serían dos líneas con el mismo nombre y nada que las distinga.
            */}
            {linea.monto != null && (
              <span className="tabular text-muted">
                {" · de "}
                {formatMoney(linea.monto, formato)}
              </span>
            )}
          </p>
          {/* Los extras, debajo del nombre: «+ Queso · + Tocineta». */}
          {(linea.opciones?.length ?? 0) > 0 && (
            <p className="mt-0.5 text-xs text-ink-soft">
              {linea.opciones!.map((o) => `+ ${o.nombre}`).join(" · ")}
            </p>
          )}
          {/*
            «Sin tomate» (2026-10-27): en rojo, como en la mesa. Con varias
            unidades en la línea, la ventana pregunta a cuántas se aplica y la
            línea se separa («1 sin tomate» y «1 completa»).
          */}
          {quitables.length > 0 ? (
            <div className="mt-0.5">
              <BotonDeSin sin={sin} onAbrir={() => setEditandoSin(true)} />
              <ModalDeSin
                abierto={editandoSin}
                plato={linea.varianteNombre ? `${linea.nombre} · ${linea.varianteNombre}` : linea.nombre}
                quitables={quitables}
                inicial={sin}
                cantidad={Number.isInteger(linea.cantidad) ? linea.cantidad : 1}
                onCerrar={() => setEditandoSin(false)}
                onGuardar={(nuevos, cuantas) => {
                  carrito.ponerSin(linea.key, nuevos, cuantas);
                  setEditandoSin(false);
                }}
              />
            </div>
          ) : (
            sin.length > 0 && (
              <p className="mt-0.5 text-xs font-semibold text-danger">
                {sin.map((q) => `Sin ${q.nombre}`).join(" · ")}
              </p>
            )
          )}
          {codigos && <CodigosDelProducto sku={codigos.sku} barcode={codigos.barcode} className="mt-0.5" />}
          {/*
            ⚠️ AL PESO SE DICE EN GRAMOS, NO EN «0,25 c/u».

            La casilla de cantidad guarda kilos porque es lo que se cobra, pero
            «0,25» no es lo que pidió nadie: el cliente dijo 250 gramos, o dos
            dólares. Sin esta línea, el cajero tiene que traducir de cabeza para
            comprobar que despachó lo correcto — que es exactamente la cuenta
            que esta pantalla vino a quitarle.
          */}
          <p className="tabular mt-0.5 text-xs text-muted">
            {unidadMenuda ? (
              <>
                {formatMoney(brutoConImpuesto, formato)} por {unidadMenuda.corta}
                <span className="font-medium text-ink-soft">
                  {" · "}
                  {comoSeDice(linea.cantidad, unidadMenuda)}
                </span>
              </>
            ) : porUnidadSuelta ? (
              // Se corta, pero se pide en su propia unidad: «1,20 por m».
              <>
                {formatMoney(brutoConImpuesto, formato)} por {porUnidadSuelta}
                <span className="font-medium text-ink-soft">
                  {" · "}
                  {linea.cantidad} {porUnidadSuelta}
                </span>
              </>
            ) : (
              // Lo de siempre para todo lo que se vende por piezas. «por
              // unidad» sonaría raro donde «c/u» ya se entiende.
              <>{formatMoney(brutoConImpuesto, formato)} c/u</>
            )}
            {/* Que se vea POR QUÉ el precio no es el de la tarjeta. */}
            {conLista && carrito.listaPreciosNombre && (
              <span className="font-medium text-accent"> · {carrito.listaPreciosNombre}</span>
            )}
          </p>

          {/*
            El aviso. Es un botón y no un texto: lo que hay que hacer con esto
            es decidir, y un aviso que no se puede pulsar obliga a buscar dónde
            se aplica.
          */}
          {/* Aquí estaba el aviso «llevas 12 · cobrar al mayor». El precio de
              mayor por cantidad se retiró: lo hace una lista de precios. */}
        </div>

        <p className="tabular shrink-0 text-sm font-semibold text-ink">
          {formatMoney(lineaTotal, formato)}
        </p>

        <button
          type="button"
          onClick={() => carrito.quitar(linea.key)}
          aria-label={`Quitar ${linea.nombre}`}
          className="-mr-1 -mt-1 shrink-0 rounded-lg p-1.5 text-faint transition-colors hover:bg-danger-soft hover:text-danger"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Cantidad */}
      <div className="mt-2 flex items-center gap-2">
        <div className="flex items-center rounded-[var(--radius-control)] border border-line-strong">
          <button
            type="button"
            onClick={() => carrito.restarUno(linea.key)}
            aria-label="Quitar uno"
            className="p-2.5 text-muted transition-colors hover:text-ink"
          >
            <Minus size={14} />
          </button>
          <input
            type="text"
            value={borradorCantidad ?? String(linea.cantidad)}
            onFocus={(e) => {
              setBorradorCantidad(String(linea.cantidad));
              // Todo seleccionado: se escribe el número nuevo sin borrar antes.
              e.currentTarget.select();
            }}
            onChange={(e) => escribirCantidad(e.target.value)}
            onBlur={() => setBorradorCantidad(null)}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            inputMode={linea.permiteDecimales ? "decimal" : "numeric"}
            autoComplete="off"
            aria-label={`Cantidad de ${linea.nombre}`}
            className="tabular w-14 border-x border-line-strong bg-transparent py-2 text-center text-sm text-ink outline-none"
          />
          <button
            type="button"
            onClick={() => carrito.sumarUno(linea.key)}
            aria-label="Agregar uno"
            className="p-2.5 text-muted transition-colors hover:text-ink"
          >
            <Plus size={14} />
          </button>
        </div>

        {puedeDescuento && !editandoDescuento && linea.descuento === 0 && (
          <button
            type="button"
            onClick={() => setEditandoDescuento(true)}
            className="rounded-lg px-2 py-1.5 text-xs text-muted transition-colors hover:text-accent"
          >
            + Descuento
          </button>
        )}

        {(editandoDescuento || linea.descuento > 0 || borradorDescuento !== null) &&
          puedeDescuento && (
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted">−</span>
            {/*
              ═══════════════════════════════════════════════════════════════
               ⚠️ NO DEJABA PONER DECIMALES (lo reportó un cliente, 2026-09-29)
              ═══════════════════════════════════════════════════════════════

              Era un `type="number"` que se redibujaba con el número ya leído:
              al escribir «1,» o «1.» la casilla volvía a «1» y la coma se
              perdía, así que 1,50 no había forma de escribirlo. Ahora guarda
              el texto tal cual mientras se escribe —como la cantidad— y
              acepta coma o punto, con hasta dos decimales.
            */}
            <Campo
              type="text"
              value={borradorDescuento ?? (linea.descuento ? String(linea.descuento) : "")}
              onFocus={(e) => {
                setBorradorDescuento(linea.descuento ? String(linea.descuento) : "");
                e.currentTarget.select();
              }}
              onChange={(e) => {
                const texto = limpiar(e.target.value.trim(), formato.code);
                if (!/^\d*([.,]\d{0,2})?$/.test(texto)) return;
                setBorradorDescuento(texto);
                const n = Number.parseFloat(texto.replace(",", "."));
                carrito.ponerDescuentoLinea(linea.key, Number.isFinite(n) ? n : 0);
              }}
              onBlur={() => setBorradorDescuento(null)}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              placeholder="0,00"
              inputMode="decimal"
              autoComplete="off"
              aria-label="Descuento"
              className="tabular h-9 w-20 text-sm"
            />
          </div>
        )}
      </div>

      {/* Quién atendió.
          Solo si hay MÁS DE UNA persona: con una sola, la respuesta es obvia y el
          backend la asigna solo. */}
      {(linea.requierePersonal || linea.kind === "service") && personal.length > 1 && (
        <div className="mt-2">
          <Lista
            value={linea.staffUserId ?? ""}
            onChange={(e) => {
              const id = e.target.value || null;
              const persona = personal.find((m) => m.user_id === id);
              carrito.ponerPersonal(
                linea.key,
                id,
                persona ? persona.display_name || persona.full_name : null,
              );
            }}
            error={linea.requierePersonal && !linea.staffUserId}
            className="h-9 text-sm"
          >
            <option value="" disabled={linea.requierePersonal}>
              {linea.requierePersonal ? "⚠️ ¿Quién atendió?" : "¿Quién atendió? (opcional)"}
            </option>
            {personal.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.display_name || m.full_name}
              </option>
            ))}
          </Lista>
        </div>
      )}
    </div>
  );
}

// =============================================================================
//  Monedas y códigos (2026-10-12)
// =============================================================================

type CodigosPorProducto = Map<string, { sku: string | null; barcode: string | null; conTallas: boolean }>;

/**
 * Los códigos de una línea de la cuenta: los de su producto.
 *
 * ⚠️ Una línea con talla no lleva los del producto: la talla tiene los suyos,
 * y enseñar el SKU del modelo junto a «M · rojo» haría creer que es el de esa
 * talla.
 */
function codigosDeLaLinea(linea: LineaCarrito, codigos: CodigosPorProducto) {
  const c = codigos.get(linea.itemId);
  if (!c || (c.conTallas && linea.variantId)) return null;
  return c.sku || c.barcode ? { sku: c.sku, barcode: c.barcode } : null;
}

/**
 * El SKU y el código de barras de un producto, en una línea pequeña y en letra
 * de código (2026-10-12).
 *
 * Lo pidió el dueño: «que en ventas salga el SKU y/o código de barras, tanto en
 * las pastillas como a la derecha, pero que quede bien». El SKU primero y más
 * oscuro (es el que se dice en voz alta); el código de barras al lado, más
 * suave (es el que se escanea). Si solo hay uno, solo ese.
 */
function CodigosDelProducto({
  sku,
  barcode,
  className,
}: {
  sku: string | null | undefined;
  barcode: string | null | undefined;
  className?: string;
}) {
  if (!sku && !barcode) return null;
  return (
    <span
      className={cx(
        "flex min-w-0 items-center gap-1.5 overflow-hidden whitespace-nowrap font-mono text-[10.5px] leading-tight",
        className,
      )}
    >
      {/* Si no caben, se corta el de barras: el SKU es el que se dice en voz alta. */}
      {sku && <span className="max-w-[60%] shrink-0 truncate font-medium text-ink-soft">{sku}</span>}
      {sku && barcode && <span className="shrink-0 text-faint" aria-hidden>·</span>}
      {barcode && <span className="min-w-0 truncate text-muted">{barcode}</span>}
    </span>
  );
}

/**
 * Lo que vale un monto en cada una de las demás monedas del negocio.
 *
 * ⚠️ TODAS, no solo la segunda: un negocio que cobra en bolívares y en pesos
 * tiene que ver las dos. Cada una con lo que de verdad se cobra en ella
 * (`equivalenteQueSeCobra`: redondeada como se pide en el mostrador).
 *
 * Con `enPila`, una debajo de otra (el total); si no, en una línea que se
 * parte donde haga falta (las tarjetas).
 *
 * Una moneda con el mismo símbolo que la principal sale con su código, «12.000
 * COP»: ver `comoFormatoJuntoA`.
 */
function Equivalencias({
  monto,
  monedas,
  principal,
  enPila = false,
  className,
}: {
  monto: string | number;
  monedas: import("@/lib/types").TenantCurrency[];
  principal: ReturnType<typeof comoFormato>;
  enPila?: boolean;
  className?: string;
}) {
  if (monedas.length === 0) return null;
  return (
    <span
      className={cx(
        "tabular flex leading-tight",
        enPila ? "flex-col items-end gap-1" : "flex-wrap gap-x-2 gap-y-0.5",
        className,
      )}
    >
      {monedas.map((m) => (
        <span key={m.currency_code} className="whitespace-nowrap">
          ≈ {formatMoney(equivalenteQueSeCobra(monto, m), comoFormatoJuntoA(m, principal))}
        </span>
      ))}
    </span>
  );
}

